/* 分析工具 — 大神追蹤器（InfoScout）
 *
 *  追蹤財經/科技 KOL（Threads / X / Substack / YouTube）的服務前端。移植自
 *  InfoScout 原生 dashboard，改用 Portal 的 HR 設計系統重寫，五個核心視圖：
 *    1. 大神牆（三欄：左神人清單 / 中貼文流 / 右推薦）
 *    2. 作者明細（中欄 — 進頁計時、離開回報閱讀停留餵統計）
 *    3. 推薦給我（右欄 — 訂閱 / 忽略；空的時候整欄塌縮）
 *    4. 追蹤統計（分頁 — 依累計閱讀停留排行，點列跳作者貼文流）
 *    5. YT 日報（分頁 — 最愛 YouTube 頻道每日基本面掃描；報告為主角：
 *       左命中報告列表（搜尋/未讀紅點/日期分組）+ 右閱讀窗（自動開最新、
 *       ↑↓切換）；掃描歷程收左欄底部摺疊段、頻道管理收 admin Modal；
 *       上游 /api/yt_daily/*）
 *
 *  資料來源：Portal 後端 proxy `/api/infoscout/*`（→ 內網 InfoScout GB10，
 *    見 backend/routes_infoscout.py）。token 只在 proxy 端注入。
 *    貼文截圖走 `/api/infoscout/media/*?token=`（<img> 無法帶 header）。
 *  時間皆 UTC ISO-8601，前端轉本地（台北）顯示。
 *
 *  2026-07-23 UI/UX 改版重點：時間窗分段快切、清單搜尋+優先度徽章、新增後
 *  自動選取、站內 ConfirmDialog、上次更新時間、貼網址自動判斷平台、OCR 支援
 *  Ctrl+V + busy 狀態 + YT 特徵自動切平台、RWD 收合、lightbox 前後張切換、
 *  統計列點擊跳轉、零帳號首次使用 CTA。
 */

// 為 handle 穩定產生一組頭像色（bg / fg），對齊 HR 編輯風柔和色盤。
const ET_AVATARS = [
  ['#E6EEED', '#0B5F5A'],
  ['#EDE7F3', '#6B4CA0'],
  ['#E3F0EA', '#1F7A5C'],
  ['#F5EAD8', '#9A6A1A'],
  ['#F5E7E0', '#B0512E'],
  ['#E4ECF5', '#2B5C8A'],
];
function ET_color(s) {
  let h = 0;
  for (let i = 0; i < (s || '').length; i++) h = (h * 31 + (s.charCodeAt(i))) >>> 0;
  return ET_AVATARS[h % ET_AVATARS.length];
}
function ET_initial(name, handle) {
  const n = (name || handle || '?').trim();
  if (/[一-鿿]/.test(n)) return n[n.length - 1];
  const parts = n.replace(/^@/, '').split(/[\s_]+/);
  return ((parts[0] || '?')[0] + ((parts[1] || '')[0] || '')).toUpperCase();
}
const ET_PLAT = {
  twitter:  { label: '𝕏', bg: '#E4ECF5', fg: '#1D6FD0' },
  threads:  { label: '脆', bg: '#EDE7F3', fg: '#6B4CA0' },
  youtube:  { label: '▶', bg: '#F7E3E3', fg: '#C0342E' },
  substack: { label: 'S', bg: '#FBF0E0', fg: '#A5691A' },
};
function ET_relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const m = Math.floor((Date.now() - d.getTime()) / 60000);
  if (m < 1) return '剛剛';
  if (m < 60) return m + ' 分鐘前';
  if (m < 1440) return Math.floor(m / 60) + ' 小時前';
  return Math.floor(m / 1440) + ' 天前';
}

// 貼文時間顯示：一律含實際日期（瀏覽器本地時區＝台北）。
//  7 天內：相對時間 + 日期時間（如「3 小時前 · 2026/07/23 09:12」）
//  更舊：只顯示日期（如「2021/02/26」）
function ET_when(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  const ymd = `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const rel = ET_relTime(iso);
  const m = Math.floor((Date.now() - d.getTime()) / 60000);
  if (m < 10080) return `${rel} · ${ymd} ${hm}`;   // 7 天內
  return ymd;
}
const ET_PRIO_LABEL = { A: 'A · 每 6 小時', B: 'B · 每 24 小時', C: 'C · 每 72 小時' };

// 貼文截圖來源改寫：外部絕對網址（YT 縮圖）直接用；相對路徑（/screenshots/*，
// InfoScout origin 根層）改走 portal 媒體代理並帶 ?token=（<img> 無法送 header）。
function ET_imgSrc(url) {
  if (!url) return null;
  if (/^https:\/\//i.test(url)) return url;
  if (url.charAt(0) === '/') {
    const tok = (typeof localStorage !== 'undefined' && localStorage.getItem('token')) || '';
    return `/api/infoscout/media${url}` + (tok ? `?token=${encodeURIComponent(tok)}` : '');
  }
  return url;
}

/* ── 官方嵌入（主要呈現方式）────────────────────────────────────────────────
 * YouTube：iframe 播放器（可播）。X / Threads：官方 embed script。
 * 嵌入失敗（刪文/斷網/被擋）→ 自動退回截圖（媒體代理），再不行才顯示（無內容）。 */

function ET_embedKind(url) {
  const u = (url || '').toLowerCase();
  if (/youtube\.com|youtu\.be/.test(u)) return 'youtube';
  if (/(twitter\.com|x\.com)\/[^/]+\/status/.test(u)) return 'tweet';
  if (/threads\.(net|com)\/@[^/]+\/post\//.test(u)) return 'threads';
  return null;
}

// 外部 embed script 只載一次（widgets.js / embed.js）
const ET_SCRIPTS = {};
function ET_loadScript(src) {
  if (!ET_SCRIPTS[src]) {
    ET_SCRIPTS[src] = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = () => resolve();
      s.onerror = () => { delete ET_SCRIPTS[src]; reject(new Error('script load failed')); };
      document.head.appendChild(s);
    });
  }
  return ET_SCRIPTS[src];
}

/* 等比縮放容器：X/Threads 官方嵌入原生上限 550px 寬 — 以 550 為基準渲染，
 * 等比放大到卡片實際寬度（滿版）。優先用 CSS zoom（Chrome/Edge：版面高度
 * 一起縮放、iframe 內容清晰），不支援才退回 transform + 高度換算。 */
function ET_ScaledBox({ children, baseWidth = 550 }) {
  const outerRef = React.useRef(null);
  const innerRef = React.useRef(null);
  const [k, setK] = React.useState(1);
  const [h, setH] = React.useState(0);
  const useZoom = React.useMemo(
    () => (typeof CSS !== 'undefined' && CSS.supports && CSS.supports('zoom', '2')), []);
  React.useEffect(() => {
    const measure = () => {
      const ow = outerRef.current ? outerRef.current.clientWidth : 0;
      // 只縮不放：官方卡最大維持原生 550px（放大會過大且糊），容器更窄才等比縮小。
      // -2px 安全邊距：避免次像素進位把右緣頂出容器（裁切）。
      const kk = ow > 0 ? Math.min(1, Math.max(0.5, (ow - 2) / baseWidth)) : 1;
      setK(kk);
      if (!useZoom && innerRef.current) setH(Math.ceil(innerRef.current.offsetHeight * kk));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (outerRef.current) ro.observe(outerRef.current);
    if (innerRef.current) ro.observe(innerRef.current);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [baseWidth, useZoom]);
  if (useZoom) {
    return (
      <div ref={outerRef} style={{ width: '100%' }}>
        {/* zoom 會同步縮放版面（含高度），不需 overflow/高度手算 */}
        <div ref={innerRef} style={{ width: baseWidth, zoom: k }}>{children}</div>
      </div>
    );
  }
  return (
    <div ref={outerRef} style={{ width: '100%', height: h || 'auto', overflow: 'hidden' }}>
      <div ref={innerRef} style={{ width: baseWidth, transform: `scale(${k})`, transformOrigin: 'top left' }}>
        {children}
      </div>
    </div>
  );
}

/* 嵌入載入占位：轉圈 + 提示，固定高度不跳版 */
function ET_EmbedLoading({ height = 140, label = '載入貼文中…' }) {
  return (
    <div style={{
      height, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      background: HR.surfaceAlt, borderRadius: uiPick(8, GxT.radius.inner), color: HR.inkFaint, fontSize: FontSizes.xxs,
    }}>
      <Spinner size={14} /> {label}
    </div>
  );
}

/* 懶掛載：捲到附近（前後 600px）才真正渲染 children（官方嵌入）。
 * 前幾篇 eager 立即渲染 → 其餘不搶網路/CPU，解「文章出現前的延遲感」。 */
function ET_LazyMount({ placeholderHeight = 220, children }) {
  const ref = React.useRef(null);
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setShow(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((en) => en.isIntersecting)) { setShow(true); io.disconnect(); }
    }, { rootMargin: '600px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [show]);
  if (show) return children;
  return <div ref={ref}><ET_EmbedLoading height={placeholderHeight} /></div>;
}

function ET_YouTubeEmbed({ url }) {
  const [loaded, setLoaded] = React.useState(false);
  const m = (url || '').match(/(?:[?&]v=|\/shorts\/|youtu\.be\/)([\w-]+)/);
  if (!m) return null;
  const shorts = /\/shorts\//i.test(url);
  return (
    // 外層夾寬（播放器適中尺寸、不撐滿寬卡），內層用 padding 比例撐高
    // （padding % 相對「父層」寬度 → 內層的父層就是被夾寬的外層，比例才正確）
    <div style={{ width: '100%', maxWidth: shorts ? 300 : 540 }}>
      <div style={{ position: 'relative', width: '100%', paddingTop: shorts ? '177.78%' : '56.25%' }}>
        {/* 載入完成前：轉圈占位（蓋在播放器上，同尺寸不跳版） */}
        {!loaded && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 1, borderRadius: uiPick(8, GxT.radius.inner),
            background: HR.surfaceAlt, display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 8, color: HR.inkFaint, fontSize: FontSizes.xxs,
          }}>
            <Spinner size={14} /> 載入影片中…
          </div>
        )}
        <iframe
          src={`https://www.youtube.com/embed/${m[1]}`}
          title="YouTube player" loading="lazy" allowFullScreen
          onLoad={() => setLoaded(true)}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, borderRadius: uiPick(8, GxT.radius.inner), background: '#000' }}
        />
      </div>
    </div>
  );
}

function ET_TweetEmbed({ url, onFail }) {
  const boxRef = React.useRef(null);
  const [ready, setReady] = React.useState(false);   // 渲染完成才顯示（先轉圈）
  React.useEffect(() => {
    let alive = true;
    const m = (url || '').match(/\/status(?:es)?\/(\d+)/);
    if (!m) { if (onFail) onFail(); return; }
    ET_loadScript('https://platform.twitter.com/widgets.js')
      .then(() => {
        if (!alive || !boxRef.current || !window.twttr || !window.twttr.widgets) throw new Error('twttr unavailable');
        boxRef.current.innerHTML = '';
        return window.twttr.widgets.createTweet(m[1], boxRef.current, {
          dnt: true, width: 550, align: 'left', lang: 'zh-tw', conversation: 'none',
        });
      })
      .then((el) => {
        if (!alive) return;
        if (el) setReady(true);
        else if (onFail) onFail();   // null = 推文不存在/被擋
      })
      .catch(() => { if (alive && onFail) onFail(); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);
  return (
    <div>
      {!ready && <ET_EmbedLoading />}
      <div ref={boxRef} style={{ opacity: ready ? 1 : 0, height: ready ? 'auto' : 0, overflow: ready ? 'visible' : 'hidden', transition: 'opacity 200ms' }} />
    </div>
  );
}

function ET_ThreadsEmbed({ url, onFail }) {
  const boxRef = React.useRef(null);
  const [ready, setReady] = React.useState(false);   // 長出 iframe 才顯示（先轉圈）
  React.useEffect(() => {
    let alive = true;
    const el = boxRef.current;
    if (!el) return;
    el.innerHTML = `<blockquote class="text-post-media" data-text-post-permalink="${url}" data-text-post-version="0" style="margin:0;min-width:100%"><a href="${url}"></a></blockquote>`;
    // Threads 的 embed.js 載入時處理未渲染的 blockquote → 每次掛新的 script 觸發
    const s = document.createElement('script');
    s.src = 'https://www.threads.net/embed.js'; s.async = true;
    document.body.appendChild(s);
    const t0 = Date.now();
    const timer = setInterval(() => {
      if (!alive) { clearInterval(timer); return; }
      if (el.querySelector('iframe')) { clearInterval(timer); setReady(true); return; }
      if (Date.now() - t0 > 6000) { clearInterval(timer); if (onFail) onFail(); }
    }, 400);
    return () => { alive = false; clearInterval(timer); try { s.remove(); } catch (e) { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);
  return (
    <div>
      {!ready && <ET_EmbedLoading />}
      <div ref={boxRef} style={{ opacity: ready ? 1 : 0, height: ready ? 'auto' : 0, overflow: ready ? 'visible' : 'hidden', transition: 'opacity 200ms' }} />
    </div>
  );
}

// 貼上的文字自動判斷平台（新增神人時免記得切下拉）
function ET_detectPlatform(text) {
  const t = (text || '').toLowerCase();
  if (/youtube\.com|youtu\.be/.test(t)) return 'youtube';
  if (/threads\.(net|com)/.test(t)) return 'threads';
  if (/(^|[^\w])(x\.com|twitter\.com)/.test(t)) return 'twitter';
  return null;
}

// 把貼文文字（InfoScout 存的是貼文容器 innerText 或截圖 OCR，混有作者名/
// 時間/互動數等 UI 行）清洗成研究主題草稿。啟發式從寬 — 使用者在「深入研究」
// modal 內還能自行修改；上限對齊 HSG DeepRequest.query 的 max_length=500。
function ET_cleanPostText(post) {
  let lines = String((post && post.text) || '').split('\n').map((l) => l.trim()).filter(Boolean);
  // X/Threads innerText 開頭常見「顯示名 / @handle / · / 時間」——前幾行出現
  // @handle 就連同其前面的顯示名一起剪掉（內文裡的 @mention 不在頭部、不受影響）。
  const hIdx = lines.slice(0, 4).findIndex((l) => /^@[\w.]+$/.test(l));
  if (hIdx >= 0) lines = lines.slice(hIdx + 1);
  const noise = (l) =>
    /^@[\w.]+$/.test(l)                                   // 落單的 @handle 行
    || /^([\d,.]+[KMB萬千]?\s*)+$/i.test(l)               // 互動數字行（讚/轉/瀏覽）
    || /^(·|…|更多|顯示更多|Show more|翻譯貼文|Translate post)$/i.test(l)
    || /^\d{1,2}(:\d{2}|\s*(小時|分鐘|秒|天|月\d{1,2}日))/.test(l)  // 時間戳行
    || /^(上午|下午)\s*\d/.test(l);
  return lines.filter((l) => !noise(l)).join('\n').trim().slice(0, 500);
}

// InfoScout 寫入慣例：成功 {ok:true}；業務失敗可能 200 {ok:false,reason} 或 4xx。
async function ET_write(path, body) {
  try {
    const res = await api('POST', path, body || {});
    if (res && res.ok === false) return { ok: false, reason: res.reason, data: res };
    return { ok: true, data: res };
  } catch (e) {
    const reason = (e && e.data && e.data.reason) || null;
    return { ok: false, reason, status: e && e.status, detail: e && e.detail };
  }
}

function ET_Avatar({ name, handle, size = 40, src }) {
  const [bg, fg] = ET_color(handle || name);
  const [failed, setFailed] = React.useState(false);
  // 有真實帳號頭像（採集時抓回的 avatar_url）就用；載入失敗退回字首圓圈。
  // referrerPolicy=no-referrer：IG/Threads CDN 會擋帶 referrer 的外連圖。
  if (src && !failed) {
    return (
      <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        style={{
          width: size, height: size, borderRadius: '50%', flexShrink: 0,
          objectFit: 'cover', background: bg, display: 'block',
        }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'grid', placeItems: 'center', background: bg, color: fg,
      fontFamily: HR.serif, fontWeight: uiPick(600, GxT.weight.title), fontSize: Math.round(size * 0.38),
    }}>{ET_initial(name, handle)}</div>
  );
}

/* Expressive-only patch for the page's hand-rolled text inputs: outlined field
 * (same look as hr-shell inputStyle under GX — card fill, 1px ol border, radius 10). */
function ET_gxField() {
  return {
    border: `1px solid ${GxT.color.ol}`, borderRadius: GxT.radius.input,
    background: GxT.color.card,
  };
}

function ET_Platforms({ list }) {
  if (!list || !list.length) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {list.map((p) => {
        const m = ET_PLAT[p];
        if (!m) return null;
        return (
          <span key={p} title={p} style={{
            fontSize: FontSizes.trading_sm, fontWeight: 700, lineHeight: 1.5,
            padding: '1px 6px', borderRadius: uiPick(4, GxT.radius.chipSm), background: m.bg, color: m.fg,
          }}>{m.label}</span>
        );
      })}
    </span>
  );
}

/* 分段快切（segmented control）— 時間窗等高頻切換用 */
function ET_Segmented({ options, value, onChange, size = 'sm' }) {
  return (
    <div style={uiStyle(
      { display: 'inline-flex', background: HR.surfaceAlt, border: `1px solid ${HR.rule}`, borderRadius: 6, padding: 2 },
      // mockup .seg: outlined segmented button, selected segment = secondary container
      // (no overflow:hidden — it would clip the focus-visible outline; end segments round themselves)
      { background: 'transparent', border: `1px solid ${GxT.color.ol}`, borderRadius: GxT.radius.btnSm, padding: 0 },
    )}>
      {options.map((o, i) => (
        <button key={o.value} onClick={() => onChange(o.value)}
          aria-pressed={uiPick(undefined, value === o.value)}
          style={uiStyle({
            border: 'none', cursor: 'pointer', fontFamily: HR.sans, fontWeight: 500,
            fontSize: size === 'sm' ? FontSizes.xxs : FontSizes.xs,
            padding: size === 'sm' ? '4px 10px' : '6px 14px', borderRadius: 4,
            background: value === o.value ? HR.surface : 'transparent',
            color: value === o.value ? HR.accent : HR.inkSoft,
            boxShadow: value === o.value ? '0 1px 2px rgba(14,20,17,0.08)' : 'none',
            whiteSpace: 'nowrap',
          }, {
            minHeight: size === 'sm' ? 32 : 40, padding: size === 'sm' ? '0 12px' : '0 16px',
            borderRadius: `${i === 0 ? 9 : 0}px ${i === options.length - 1 ? 9 : 0}px ${i === options.length - 1 ? 9 : 0}px ${i === 0 ? 9 : 0}px`,
            boxShadow: 'none', fontWeight: GxT.weight.label,
            borderLeft: i > 0 ? `1px solid ${GxT.color.ol}` : 'none',
            background: value === o.value ? GxT.color.sc : 'transparent',
            color: value === o.value ? GxT.color.onSc : GxT.color.onV,
          })}>{o.label}</button>
      ))}
    </div>
  );
}

/* RWD：三欄在窄視窗收合（<1280 兩欄、推薦欄移到下方；<920 單欄）。
 * 用 !important 蓋過 inline grid-template-columns。只注入一次。 */
if (typeof document !== 'undefined' && !document.getElementById('et-rwd-style')) {
  const s = document.createElement('style');
  s.id = 'et-rwd-style';
  s.textContent = `
    /* 三欄高度填滿視窗剩餘空間；欄內 header 固定、捲動區 flex 撐滿 */
    .et-cols { height: calc(100vh - 300px); min-height: 480px; }
    .et-fill { height: 100%; display: flex; flex-direction: column; }
    .et-fill > div:last-child { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; }
    .et-scroll { flex: 1 1 auto; min-height: 0; overflow-y: auto; }
    .et-recs { height: 100%; }
    @media (max-width: 1280px) {
      .et-cols { grid-template-columns: 280px minmax(0,1fr) !important; }
      .et-recs { grid-column: 1 / -1; height: auto; }
    }
    @media (max-width: 920px) {
      .et-cols { grid-template-columns: 1fr !important; height: auto; }
      .et-scroll { max-height: 480px; }
    }
    /* YT 日報：左報告列表 / 右閱讀窗，窄視窗收合單欄 */
    .et-yd-cols { display: grid; grid-template-columns: 340px minmax(0,1fr); gap: 14px; align-items: start; }
    @media (max-width: 920px) {
      .et-yd-cols { grid-template-columns: 1fr !important; }
    }
  `;
  document.head.appendChild(s);
}

/* ── 深入研究 modal ──────────────────────────────────────────────
 * 貼文 → HSG 研究引擎：清洗貼文文字預填「研究主題」textarea（≤500 字，
 * 對齊上游 DeepRequest.query），使用者修剪成研究問題後，
 * 「在研究引擎開啟 ↗」= openResearchEngineTab(toast, query) 帶 ?q= 深連結
 * 開新分頁 —— 研究在 HSG 自家 UI 進行（進度/報告/下載/金鑰管理都用它現成
 * 的；金鑰為使用者在該頁自設的個人金鑰）。
 * 原「站內深度研究」（POST deep + 進度輪詢 + 站內渲染報告）因 HSG server
 * 端無金鑰、portal 注入 env 未配置而暫時移除（2026-08-06 Ray 指示）——
 * 後端 _inject_llm_defaults 注入機制保留備用，要恢復站內流程時見
 * git history 的 ET_ResearchModal 完整版。
 * 權限：按鈕入口已依 canAccessRoute('research-engine') gate（員工限定），
 * 非員工看不到入口、後端閘門亦 default-deny。 */
function ET_ResearchModal({ post, onClose, toast }) {
  const [query, setQuery] = React.useState(() => ET_cleanPostText(post));

  function openEngine() {
    if (window.openResearchEngineTab) window.openResearchEngineTab(toast, query.trim());
    onClose();
  }

  return (
    <Modal
      open onClose={onClose} size="lg"
      title="深入研究"
      subtitle={post.url ? `來源貼文：${post.url}` : '來源：大神追蹤器貼文'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>取消</Button>
          <Button variant="primary" icon="ext" disabled={!query.trim()} onClick={openEngine}>在研究引擎開啟</Button>
        </>
      }
    >
      <Field label="研究主題" hint={`已從貼文內容帶入（自動去除作者/時間/互動數等雜訊）；建議修剪成一句研究問題。${query.length}/500 字`}>
        <Textarea value={query} onChange={(v) => setQuery(String(v).slice(0, 500))} rows={7} />
      </Field>
      <p style={{ fontSize: FontSizes.trading_xl, color: HR.inkFaint, margin: '10px 0 0' }}>
        會帶著主題開啟研究引擎頁，用你在該頁設定的模型與 API 金鑰進行深度研究。
      </p>
    </Modal>
  );
}

/* ── 生成教材 modal（YouTube 貼文 → YTKB 分級教材）─────────────────
 * 走 portal 的 YTKB 代理（/api/ytkb，見 backend/routes_ytkb.py）打上游
 * master 版的分級教材管線：
 *   POST /api/vplus/generate { url, tiers } → { job_id }（immediately）
 *   GET  /api/vplus/jobs/{job_id} → { status, stage, detail, … } 輪詢
 * 認證是「雙層」：
 *   1. portal 層（**必要**）— modal 開啟時先 POST /api/ytkb/session 換
 *      ytkb_session cookie（proxy cookie 優先，之後瀏覽器自動附帶）——
 *      未登入 portal 的人打不進來；
 *   2. YTKB 層（**視上游開關**）— 上游認證可開關（YTKB_AUTH_MODE；
 *      2026-08-06 起 GB10 :8010 免登入 → portal 登入即可用，無需 YTKB
 *      登入步驟）。localStorage['ytkb.auth.token'] 有就帶（上游若開認證
 *      沿用），沒有就裸打；上游回 401 才進登入引導（後備）。
 * job 是上游背景任務 — 關掉 modal 不影響生成，成品在影音知識庫
 * 「分級教材」頁；輪詢只在 modal 開著時進行。
 * ⚠️ 前置：GB10 的 YTKB 需為含 /api/vplus 的 master 版（舊版 404）。 */
const ET_YTKB_TOKEN_KEY = 'ytkb.auth.token';   // 與上游 dashboard/auth.jsx 的 TOKEN_KEY 一致

function ET_ytkbToken() {
  try { return localStorage.getItem(ET_YTKB_TOKEN_KEY) || ''; } catch (e) { return ''; }
}

// 打 YTKB 上游 API（經 portal 代理）。portal 身分走 ytkb_session cookie
// （呼叫前需先換好）；localStorage 有 YTKB token 就帶上（原樣轉發上游）、
// 沒有就裸打 —— 上游認證是可開關的（YTKB_AUTH_MODE；2026-08-06 起 GB10
// :8010 免登入），開著沒 token 上游會回 401，這時才進登入引導
// （err.needYtkbLogin）。不能在前端先擋「無 token」，否則上游免登入時
// 會被自己的檢查冤枉擋下。
// ⚠️ 帶了 token 被 401 ＝ token 無效（典型：舊 :8000 實例發的殘留 token，
// 免登入的上游對「沒帶」放行、對「帶了壞的」照樣拒）→ 清掉殘留 token
// 裸打**重試一次**自我修復；重試仍 401（= 上游真的開著認證）才進登入引導。
async function ET_ytkbApi(method, path, body) {
  const _once = async (token) => {
    const headers = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const init = { method, headers };
    if (body !== undefined && body !== null) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    return fetch(`/api/ytkb${path}`, init);
  };

  const token = ET_ytkbToken();
  let res = await _once(token);
  if (res.status === 401 && token) {
    try { localStorage.removeItem(ET_YTKB_TOKEN_KEY); } catch (e) { /* noop */ }
    res = await _once('');
  }
  let data = null;
  try { data = await res.json(); } catch (e) { /* 非 JSON 回應 */ }
  if (res.status === 401) {
    const err = new Error((data && data.detail) || '影音知識庫登入已過期');
    err.needYtkbLogin = true;
    throw err;
  }
  if (res.status === 404) {
    // 舊版上游沒有 /api/vplus —— 給明確的部署訊息而不是「Not Found」
    const err = new Error('影音知識庫尚未更新到支援分級教材的版本（/api/vplus 不存在），請先更新 GB10 的 YTKB');
    throw err;
  }
  if (!res.ok) {
    const err = new Error((data && (data.detail || data.error)) || `影音知識庫回應 HTTP ${res.status}`);
    throw err;
  }
  return data;
}

const ET_TIERS = [
  { key: 'T1', label: 'T1 專業版', hint: '從業人員導向' },
  { key: 'T2', label: 'T2 一般版', hint: '30 歲一般成人' },
  { key: 'T3', label: 'T3 兒童版', hint: '10 歲小朋友' },
];

function ET_CoursewareModal({ post, onClose, toast }) {
  const [tiers, setTiers] = React.useState(['T1', 'T2', 'T3']);
  // phase: edit | login（需要 YTKB 登入）| running | done | error
  const [phase, setPhase] = React.useState('edit');
  const [error, setError] = React.useState('');
  const [job, setJob] = React.useState(null);     // { job_id, status, stage, detail }
  const poll = React.useRef(null);
  const alive = React.useRef(true);

  // portal 層 cookie 先換好（背景，失敗到 submit 時自然會浮出）
  React.useEffect(() => {
    api('POST', '/ytkb/session').catch(() => {});
    return () => { alive.current = false; if (poll.current) clearInterval(poll.current); };
  }, []);

  function toggleTier(k) {
    setTiers((cur) => (cur.includes(k) ? cur.filter((t) => t !== k) : [...cur, k]));
  }

  function startPolling(jobId) {
    poll.current = setInterval(() => {
      ET_ytkbApi('GET', `/api/vplus/jobs/${encodeURIComponent(jobId)}`)
        .then((d) => {
          if (!alive.current || !d) return;
          setJob({ job_id: jobId, ...d });
          if (d.status === 'success') { clearInterval(poll.current); setPhase('done'); }
          else if (d.status === 'failed') {
            clearInterval(poll.current);
            setError(d.error || d.detail || '生成失敗');
            setPhase('error');
          }
        })
        .catch(() => { /* 單次輪詢失敗容忍（網路抖動）；job 在上游照跑 */ });
    }, 3000);
  }

  function submit() {
    if (!tiers.length) return;
    setError('');
    ET_ytkbApi('POST', '/api/vplus/generate', { url: post.url, tiers })
      .then((d) => {
        if (!alive.current) return;
        setJob({ job_id: d.job_id, status: 'running', stage: 'queued', detail: '已排入佇列' });
        setPhase('running');
        startPolling(d.job_id);
      })
      .catch((e) => {
        if (!alive.current) return;
        if (e.needYtkbLogin) { setPhase('login'); return; }
        setError(e.message || '生成教材排程失敗');
        setPhase('error');
      });
  }

  const running = phase === 'running';
  const openYtkb = () => { if (window.openYtkbTab) window.openYtkbTab(toast); };

  return (
    <Modal
      open onClose={onClose} size="md"
      title="生成分級教材"
      subtitle={`影片：${post.url || ''}`}
      footer={
        phase === 'edit' ? (
          <>
            <Button variant="ghost" onClick={onClose}>取消</Button>
            <Button variant="primary" icon="book" disabled={!tiers.length} onClick={submit}>排入生成</Button>
          </>
        ) : phase === 'login' ? (
          <>
            <Button variant="ghost" onClick={onClose}>取消</Button>
            <Button variant="secondary" icon="ext" onClick={openYtkb}>開啟影音知識庫登入</Button>
            <Button variant="primary" onClick={submit}>已登入，重試</Button>
          </>
        ) : running ? (
          <>
            <Button variant="ghost" onClick={onClose}>關閉（生成會在背景繼續）</Button>
            <Button variant="secondary" icon="ext" onClick={openYtkb}>開啟影音知識庫</Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>關閉</Button>
            {phase === 'error' && <Button variant="secondary" onClick={() => setPhase('edit')}>返回重試</Button>}
            <Button variant="primary" icon="ext" onClick={openYtkb}>開啟影音知識庫查看教材</Button>
          </>
        )
      }
    >
      {phase === 'edit' && (
        <>
          <Field label="教材分級" hint="可複選；生成含影片下載/轉錄/畫面理解，需時較長，成品在影音知識庫「分級教材」頁">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
              {ET_TIERS.map((t) => (
                <Checkbox
                  key={t.key}
                  checked={tiers.includes(t.key)}
                  onChange={() => toggleTier(t.key)}
                  label={`${t.label}（${t.hint}）`}
                />
              ))}
            </div>
          </Field>
        </>
      )}

      {phase === 'login' && (
        <p style={{ fontSize: FontSizes.xs, color: HR.inkSoft, lineHeight: 1.8, margin: '8px 0' }}>
          生成教材需要影音知識庫（YTKB）的帳號登入。請先點「開啟影音知識庫登入」，
          在新分頁完成登入後回到這裡按「已登入，重試」。
        </p>
      )}

      {(running || phase === 'done') && job && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 0', fontSize: FontSizes.xs }}>
          {phase === 'done'
            ? <span style={{ color: HR.accent }}>✓</span>
            : <Spinner size={16} />}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 500 }}>
              {phase === 'done' ? '教材已生成完成' : (job.detail || job.stage || '生成中…')}
            </div>
            <div style={{ color: HR.inkFaint, fontSize: FontSizes.trading_lg, fontFamily: HR.mono, marginTop: 2 }}>
              任務 {job.job_id}
            </div>
          </div>
        </div>
      )}

      {phase === 'error' && (
        <div style={{ fontSize: FontSizes.xs, color: uiPick('#B0512E', GxT.color.down), padding: '12px 0', whiteSpace: 'pre-wrap' }}>
          {error}
        </div>
      )}
    </Modal>
  );
}

function ExpertTrackerPage() {
  const toast = useToast();
  const [tab, setTab] = React.useState('wall');   // 'wall' | 'stats' | 'ytdaily'
  // 權限由上游 InfoScout 依「這個 Portal 使用者」回答（GET /api/infoscout/access）：
  //   can_write — 可編輯自己的清單（多帳號版：每個登入者都有自己的清單，一般員工也可寫）
  //   is_admin  — 全域操作（自動更新排程、YT 日報排程 / 立即執行）
  // 上游還沒升級到多帳號版時 /access 只有 {role, can_write}；is_admin 缺就退回 Portal 自己的 admin 判定。
  const [access, setAccess] = React.useState(null);
  React.useEffect(() => {
    api('GET', '/infoscout/access').then(setAccess).catch(() => setAccess({}));
  }, []);
  const portalAdmin = (typeof isAdmin === 'function') ? isAdmin(window._USER) : false;
  const canWrite = access ? access.can_write === true : false;
  const isAdminUser = access
    ? (typeof access.is_admin === 'boolean' ? access.is_admin : portalAdmin)
    : false;
  // 深入研究（→ 研究引擎）：員工限定 — research-engine 不在非員工路由白名單
  // （後端閘門亦 default-deny），非員工連入口都不顯示。
  const canResearch = (typeof canAccessRoute === 'function') && canAccessRoute(window._USER, 'research-engine');
  const [researchPost, setResearchPost] = React.useState(null);
  // 生成教材（YouTube 貼文 → YTKB 分級教材）：權限對齊「影音知識庫」入口
  // （analyst 兩級/non_staff 皆可見；上游 YTKB 另有自己的登入把關）。
  const canCourseware = (typeof canAccessRoute === 'function') && canAccessRoute(window._USER, 'ytkb');
  const [coursewarePost, setCoursewarePost] = React.useState(null);

  // 大神牆篩選
  const [categories, setCategories] = React.useState([]);
  const [cat, setCat] = React.useState('');
  const [sort, setSort] = React.useState('latest');
  const [showDisabled, setShowDisabled] = React.useState(false);

  const [experts, setExperts] = React.useState(null);   // null = 載入中
  const [selHandle, setSelHandle] = React.useState(null);
  const [subCount, setSubCount] = React.useState(null);

  // 中欄貼文 — 預設「過去 7 天」（增量時效門檻也是 3 天，近況優先）
  const [postWin, setPostWin] = React.useState('7d');
  const [postState, setPostState] = React.useState({ loading: false, meta: null, posts: [] });

  // 右欄推薦
  const [recs, setRecs] = React.useState(null);

  // 立即更新 / 排程 / 上次更新時間
  const [running, setRunning] = React.useState(false);
  const [schedule, setSchedule] = React.useState(null);
  const [lastRunAt, setLastRunAt] = React.useState(null);
  const runPoll = React.useRef(null);

  // 新增神人面板
  const [addOpen, setAddOpen] = React.useState(false);
  const [addText, setAddText] = React.useState('');
  const [addPriority, setAddPriority] = React.useState('B');
  const [addPlatform, setAddPlatform] = React.useState('twitter');
  const [addBusy, setAddBusy] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const [ocrBusy, setOcrBusy] = React.useState(false);
  const [platformFlash, setPlatformFlash] = React.useState(false);
  const fileRef = React.useRef(null);
  const flashTimer = React.useRef(null);

  // ⋯ 選單 / 移除確認
  const [menuFor, setMenuFor] = React.useState(null);
  const [confirmRemove, setConfirmRemove] = React.useState(null);
  const [removeBusy, setRemoveBusy] = React.useState(false);

  // 統計
  const [stats, setStats] = React.useState(null);

  // 閱讀停留計時（作者切換 / 離開頁面時回報，餵追蹤統計）
  const dwell = React.useRef({ handle: null, start: 0 });
  const flushDwell = React.useCallback(() => {
    const { handle, start } = dwell.current;
    if (handle && start) {
      const ms = Date.now() - start;
      if (ms > 800) { ET_write('/infoscout/engagement', { handle, dwell_ms: ms }); }
    }
    dwell.current = { handle: null, start: 0 };
  }, []);

  const selected = React.useMemo(
    () => (experts || []).find((e) => e.handle === selHandle) || null,
    [experts, selHandle],
  );

  // ── loaders ──
  const loadWall = React.useCallback(() => {
    setExperts(null);
    const qs = `category=${encodeURIComponent(cat)}&window=1d&sort=${sort}&show_disabled=${showDisabled}`;
    api('GET', `/infoscout/experts?${qs}`)
      .then((d) => {
        const list = (d && d.experts) || [];
        setExperts(list);
        setSelHandle((cur) => (cur && list.some((e) => e.handle === cur) ? cur : (list[0] && list[0].handle) || null));
      })
      .catch((e) => { toast((e && e.detail) || '大神清單載入失敗', 'error'); setExperts([]); });
  }, [cat, sort, showDisabled, toast]);

  const loadRecs = React.useCallback(() => {
    api('GET', '/infoscout/recommendations')
      .then((d) => setRecs((d && d.recommendations) || []))
      .catch(() => setRecs([]));
  }, []);

  const refreshHealth = React.useCallback(() => {
    api('GET', '/infoscout/health').then((h) => setSubCount(h && h.experts)).catch(() => {});
  }, []);

  // boot: categories + schedule + run 狀態（含上次更新時間）+ wall + recs
  React.useEffect(() => {
    api('GET', '/infoscout/categories')
      .then((d) => setCategories((d && d.categories) || []))
      .catch(() => setCategories([]));
    api('GET', '/infoscout/schedule').then(setSchedule).catch(() => {});
    api('GET', '/infoscout/run/status').then((s) => {
      if (s && s.finished_at) setLastRunAt(s.finished_at);
      if (s && s.running) { setRunning(true); startRunPoll(); }
    }).catch(() => {});
    refreshHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => { loadWall(); }, [loadWall]);
  React.useEffect(() => { loadRecs(); }, [loadRecs]);

  // 卸載時 flush 閱讀停留、清掉輪詢與 flash timer
  React.useEffect(() => () => {
    flushDwell();
    if (runPoll.current) clearInterval(runPoll.current);
    if (flashTimer.current) clearTimeout(flashTimer.current);
  }, [flushDwell]);

  // 選取作者 → flush 前一位、開始計時、載入貼文
  React.useEffect(() => {
    if (!selHandle) { setPostState({ loading: false, meta: null, posts: [] }); return; }
    flushDwell();
    dwell.current = { handle: selHandle, start: Date.now() };
    let alive = true;
    setPostState((s) => ({ ...s, loading: true }));
    api('GET', `/infoscout/expert/${encodeURIComponent(selHandle)}/posts?window=${postWin}`)
      .then((d) => { if (alive) setPostState({ loading: false, meta: d, posts: (d && d.posts) || [] }); })
      .catch((e) => { if (alive) { setPostState({ loading: false, meta: null, posts: [] }); toast((e && e.detail) || '貼文載入失敗', 'error'); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selHandle, postWin]);

  // 離開大神牆分頁時 flush 閱讀停留；切到統計分頁時載入統計
  React.useEffect(() => {
    if (tab === 'wall') return;
    flushDwell();
    if (tab !== 'stats') return;
    setStats(null);
    api('GET', '/infoscout/stats?window=30d').then(setStats).catch(() => setStats({ ranked: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // 新增面板開啟時支援 Ctrl+V 貼上截圖（OCR）
  React.useEffect(() => {
    if (!addOpen) return;
    const onPaste = (e) => {
      if (ocrBusy) return;
      const items = (e.clipboardData && e.clipboardData.items) || [];
      for (const it of items) {
        if (it.type && it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f) { e.preventDefault(); onPickImage(f); return; }
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addOpen, ocrBusy]);

  // ── 立即更新 ──
  function startRunPoll() {
    if (runPoll.current) return;
    runPoll.current = setInterval(async () => {
      let s;
      try { s = await api('GET', '/infoscout/run/status'); } catch { return; }
      if (s && s.running) return;
      clearInterval(runPoll.current); runPoll.current = null;
      setRunning(false);
      if (s && s.finished_at) setLastRunAt(s.finished_at);
      if (s && s.summary) toast('更新完成：' + s.summary, 'success');
      loadWall(); loadRecs(); refreshHealth();
    }, 3000);
  }
  async function onRunNow() {
    setRunning(true);
    const res = await ET_write('/infoscout/run', {});
    if (!res.ok && res.reason === 'already_running') toast('已經在更新中…');
    else toast('開始增量更新（只抓 3 天內的新貼文，約需數分鐘）');
    startRunPoll();
  }

  // ── 排程 ──
  async function onScheduleChange(v) {
    const enabled = v !== 'off';
    const interval_hours = enabled ? parseInt(v, 10) : 6;
    const res = await ET_write('/infoscout/schedule', { enabled, interval_hours });
    if (res.ok) {
      setSchedule({ ...(schedule || {}), enabled, interval_hours });
      toast(enabled ? `背景自動更新：每 ${interval_hours} 小時` : '已關閉背景自動更新', 'success');
    } else toast('排程設定失敗', 'error');
  }

  // ── 新增神人 ──
  function flashPlatform() {
    setPlatformFlash(true);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setPlatformFlash(false), 2400);
  }
  // 輸入/貼上時自動判斷平台（youtube/threads/x 網址 → 自動切換並高亮提醒）
  function onAddTextChange(v) {
    setAddText(v);
    const det = ET_detectPlatform(v);
    if (det && det !== addPlatform) { setAddPlatform(det); flashPlatform(); }
  }
  async function onAddSubmit() {
    const raw = addText.trim();
    if (!raw) { toast('請輸入帳號'); return; }
    const tokens = raw.split(/[\s,\n]+/).filter(Boolean);
    setAddBusy(true);
    try {
      if (tokens.length <= 1) {
        const res = await ET_write('/infoscout/author/add', { handle: tokens[0] || raw, priority: addPriority, platform: addPlatform });
        if (res.ok) {
          const h = (res.data && res.data.handle) || tokens[0];
          toast(`已新增 @${h}`, 'success');
          setAddText('');
          setSelHandle(h);            // 新增後自動選取 → 貼文流直接切過去
          loadWall(); refreshHealth();
        } else toast('新增失敗：' + ET_addReason(res.reason), 'error');
      } else {
        const res = await ET_write('/infoscout/author/add_bulk', { text: raw, priority: addPriority, platform: addPlatform });
        if (res.ok) {
          const d = res.data || {};
          toast(`新增 ${(d.added || []).length} 位，略過 ${(d.skipped || []).length} 位，無效 ${(d.invalid || []).length} 位`, 'success');
          setAddText('');
          if ((d.added || []).length) setSelHandle(d.added[0]);
          loadWall(); refreshHealth();
        } else toast('批次新增失敗', 'error');
      }
    } finally { setAddBusy(false); }
  }
  function ET_addReason(r) {
    return ({ already_subscribed: '已經訂閱過', invalid_handle: '帳號格式無效', write_failed: '寫入失敗' })[r] || r || '';
  }
  function onPickImage(file) {
    if (!file || ocrBusy) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setOcrBusy(true);
      try {
        const res = await ET_write('/infoscout/author/ocr_handles', { image_b64: reader.result });
        if (res.ok && res.data && res.data.handles && res.data.handles.length) {
          setAddText(res.data.handles.join(' '));
          // 截圖含 YT 特徵字樣 → 自動切 youtube（並高亮提醒確認）
          const raw = (res.data.raw_text || '');
          if (/訂閱者|部影片|youtube|shorts/i.test(raw) && addPlatform !== 'youtube') {
            setAddPlatform('youtube');
          }
          flashPlatform();
          toast(`OCR 解析到 ${res.data.handles.length} 個帳號，確認平台後按新增`, 'success');
        } else if (res.ok) {
          toast('截圖中找不到 @帳號，請改用手動輸入');
        } else {
          const map = { ocr_unavailable: 'OCR 未啟用，請手動輸入帳號', ocr_no_text: 'OCR 沒讀到文字', ocr_error: 'OCR 連線失敗', bad_image: '圖片無法解析' };
          toast(map[res.reason] || 'OCR 失敗', 'error');
        }
      } finally { setOcrBusy(false); }
    };
    reader.readAsDataURL(file);
  }

  // ── ⋯ 動作（移除改走站內 ConfirmDialog）──
  async function onExpertAction(act, handle) {
    setMenuFor(null);
    if (act === 'disable' || act === 'enable') {
      const res = await ET_write('/infoscout/author/toggle', { handle, disabled: act === 'disable' });
      if (res.ok) { toast(act === 'disable' ? `已停用 @${handle}` : `已復原 @${handle}`, 'success'); loadWall(); }
      else toast('操作失敗：' + (res.reason || ''), 'error');
    } else if (act === 'remove') {
      setConfirmRemove(handle);
    }
  }
  async function doRemove() {
    const handle = confirmRemove;
    if (!handle) return;
    setRemoveBusy(true);
    const res = await ET_write('/infoscout/author/remove', { handle });
    setRemoveBusy(false);
    setConfirmRemove(null);
    if (res.ok) { toast(`已移除 @${handle}`, 'success'); loadWall(); refreshHealth(); }
    else toast('操作失敗：' + (res.reason || ''), 'error');
  }
  async function onPriorityChange(handle, priority) {
    const res = await ET_write('/infoscout/author/priority', { handle, priority });
    if (res.ok) { toast(`@${handle} 更新頻率改為 ${priority}`, 'success'); loadWall(); }
    else toast('修改失敗：' + (res.reason || ''), 'error');
  }

  // ── 推薦：訂閱 / 忽略 ──
  async function onRecAction(act, handle) {
    const res = await ET_write(act === 'sub' ? '/infoscout/subscribe' : '/infoscout/dismiss', { handle });
    if (res.ok) {
      toast(act === 'sub' ? `已訂閱 @${handle}` : `已忽略 @${handle}`, 'success');
      setRecs((rs) => (rs || []).filter((r) => r.handle !== handle));
      if (act === 'sub') { setSelHandle(handle); loadWall(); refreshHealth(); }
    } else toast('操作失敗：' + (res.reason || ''), 'error');
  }

  // 統計列點擊 → 回大神牆並選取該作者
  function openAuthor(handle) {
    setTab('wall');
    setSelHandle(handle);
  }

  // ── 大神牆 YouTube 貼文 →「生成日報」──
  // 單片手動走 yt_daily 完整管線(上游 POST /api/yt_daily/process,--force:
  // 忽略最愛/掃描窗/短片門檻、不動游標)。admin 專屬(代理非 GET 限 admin)。
  // 已有報告 → 直接切到 YT 日報分頁打開該報告(ytdFocusId)。
  const [ytdFocusId, setYtdFocusId] = React.useState(null);
  async function onYtDailyGen(post) {
    const res = await ET_write('/infoscout/yt_daily/process', {
      url: post.url,
      handle: (selected && selected.handle) || selHandle || '',
      title: String(post.text || '').split('\n')[0].slice(0, 200),
      published_at: post.posted_at || '',
    });
    if (res.ok && res.data && res.data.reason === 'already_done') {
      setYtdFocusId(res.data.video_id);
      setTab('ytdaily');
      toast('這部影片已有日報,已為你打開', 'success');
    } else if (res.ok) {
      toast('已開始生成日報(轉錄+判定約需數分鐘~數十分鐘),完成後到 YT 日報分頁查看', 'success');
    } else {
      const map = {
        bad_url: '無法從貼文網址解析出影片',
        deleted: '這部影片的日報先前已被刪除(軟刪不復活)',
        already_running: '掃描器正在執行其他任務,請稍後再試',
      };
      toast('生成日報失敗:' + (map[res.reason] || res.reason || res.detail || ''), 'error');
    }
  }

  // 關閉 ⋯ 選單：點任意處 / Esc
  React.useEffect(() => {
    if (!menuFor) return;
    const close = () => setMenuFor(null);
    const onKey = (e) => { if (e.key === 'Escape') setMenuFor(null); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('click', close); document.removeEventListener('keydown', onKey); };
  }, [menuFor]);

  return (
    <>
      <PageHeader
        eyebrow="分析工具 · InfoScout"
        title="大神追蹤器"
        italic={subCount != null ? `追蹤 ${subCount} 位` : ''}
        action={
          <ET_Segmented
            size="md" value={tab} onChange={setTab}
            options={[{ value: 'wall', label: '大神牆' }, { value: 'ytdaily', label: 'YT 日報' }, { value: 'stats', label: '追蹤統計' }]}
          />
        }
      />

      {tab === 'wall' ? (
        <ET_Wall
          canWrite={canWrite} isAdmin={isAdminUser}
          categories={categories} cat={cat} setCat={setCat} sort={sort} setSort={setSort}
          showDisabled={showDisabled} setShowDisabled={setShowDisabled}
          experts={experts} selHandle={selHandle} setSelHandle={setSelHandle}
          selected={selected} postWin={postWin} setPostWin={setPostWin} postState={postState}
          recs={recs} onRecAction={onRecAction}
          running={running} onRunNow={onRunNow} lastRunAt={lastRunAt}
          schedule={schedule} onScheduleChange={onScheduleChange}
          addOpen={addOpen} setAddOpen={setAddOpen}
          addText={addText} onAddTextChange={onAddTextChange}
          addPriority={addPriority} setAddPriority={setAddPriority}
          addPlatform={addPlatform} setAddPlatform={setAddPlatform}
          platformFlash={platformFlash}
          addBusy={addBusy} onAddSubmit={onAddSubmit}
          dragOver={dragOver} setDragOver={setDragOver} fileRef={fileRef}
          onPickImage={onPickImage} ocrBusy={ocrBusy}
          menuFor={menuFor} setMenuFor={setMenuFor} onExpertAction={onExpertAction} onPriorityChange={onPriorityChange}
          onResearch={canResearch ? setResearchPost : null}
          onCourseware={canCourseware ? setCoursewarePost : null}
          onYtDaily={canWrite ? onYtDailyGen : null}
        />
      ) : tab === 'stats' ? (
        <ET_Stats stats={stats} onOpenAuthor={openAuthor} />
      ) : (
        <ET_YtDaily canWrite={canWrite} isAdmin={isAdminUser} toast={toast} focusId={ytdFocusId} />
      )}

      {researchPost && (
        <ET_ResearchModal post={researchPost} onClose={() => setResearchPost(null)} toast={toast} />
      )}
      {coursewarePost && (
        <ET_CoursewareModal post={coursewarePost} onClose={() => setCoursewarePost(null)} toast={toast} />
      )}

      <ConfirmDialog
        open={!!confirmRemove}
        title={confirmRemove ? `移除 @${confirmRemove}` : ''}
        message="此動作會從追蹤清單刪除該帳號（已收集的貼文保留在資料庫）。"
        danger busy={removeBusy} confirmLabel="確認移除"
        onCancel={() => setConfirmRemove(null)}
        onConfirm={doRemove}
      />
    </>
  );
}

// ───────────────────────── 大神牆 ─────────────────────────
function ET_Wall(p) {
  const catOptions = [{ value: '', label: `全部類別` }, ...p.categories.map((c) => ({ value: c, label: c }))];
  const sortOptions = [
    { value: 'latest', label: '最新貼文' },
    { value: 'most', label: '最多貼文' },
    { value: 'name', label: '名稱排序' },
  ];
  const schedVal = p.schedule ? (p.schedule.enabled ? String(p.schedule.interval_hours) : 'off') : '6';
  // 推薦欄空的時候整欄塌縮（載入中仍保留骨架欄）。左欄 340px（清單可讀性），
  // 貼文流填滿剩餘寬度。
  const recsVisible = p.recs === null || (p.recs && p.recs.length > 0);
  const gridCols = recsVisible ? '340px minmax(0,1fr) 320px' : '340px minmax(0,1fr)';

  return (
    <>
      {/* 工具列 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ width: 180 }}>
          <Select value={p.cat} allowEmpty={false} onChange={(v) => p.setCat(v || '')} options={catOptions} />
        </div>
        <div style={{ width: 150 }}>
          <Select value={p.sort} allowEmpty={false} onChange={(v) => p.setSort(v || 'latest')} options={sortOptions} />
        </div>
        <Checkbox checked={p.showDisabled} onChange={p.setShowDisabled} label="顯示已停用" />
        <span style={{ flex: 1 }} />
        {p.lastRunAt && (
          <span title={p.lastRunAt} style={{ fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkFaint, whiteSpace: 'nowrap' }}>
            上次更新 {ET_relTime(p.lastRunAt)}
          </span>
        )}
        {p.canWrite && (
          <>
            {p.isAdmin && (
              <div style={{ width: 168 }}>
                <Select
                  value={schedVal} allowEmpty={false} onChange={p.onScheduleChange}
                  options={[
                    { value: '6', label: '自動：每 6 小時' },
                    { value: '3', label: '自動：每 3 小時' },
                    { value: '1', label: '自動：每 1 小時' },
                    { value: 'off', label: '自動更新：關閉' },
                  ]}
                />
              </div>
            )}
            <Button variant="ghost" size="sm" icon="refresh" onClick={p.onRunNow} disabled={p.running}>
              {p.running ? '更新中…' : (p.isAdmin ? '立即更新' : '更新我的清單')}
            </Button>
            <Button variant="primary" size="sm" icon="plus" onClick={() => p.setAddOpen((o) => !o)}>新增神人</Button>
          </>
        )}
      </div>

      {p.addOpen && p.canWrite && <ET_AddPanel {...p} />}

      <div className="et-cols" style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 18, alignItems: 'stretch' }}>
        {/* 左：神人清單 */}
        <ET_ExpertList
          canWrite={p.canWrite}
          experts={p.experts} selHandle={p.selHandle} setSelHandle={p.setSelHandle}
          menuFor={p.menuFor} setMenuFor={p.setMenuFor} onExpertAction={p.onExpertAction}
          onOpenAdd={() => p.setAddOpen(true)}
        />

        {/* 中：貼文流 */}
        <ET_Feed
          canWrite={p.canWrite}
          selected={p.selected} postWin={p.postWin} setPostWin={p.setPostWin}
          postState={p.postState} onPriorityChange={p.onPriorityChange}
          onResearch={p.onResearch} onCourseware={p.onCourseware}
          onYtDaily={p.onYtDaily}
        />

        {/* 右：推薦（空則塌縮） */}
        {recsVisible && (
          <div className="et-recs">
            <ET_Recs canWrite={p.canWrite} recs={p.recs} onRecAction={p.onRecAction} />
          </div>
        )}
      </div>
    </>
  );
}

function ET_AddPanel(p) {
  return (
    <div style={uiStyle(
      { background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6, padding: 16, marginBottom: 16 },
      { border: 'none', borderRadius: GxT.radius.card, padding: 20 },
    )}>
      <textarea
        value={p.addText} onChange={(e) => p.onAddTextChange(e.target.value)}
        placeholder="貼上 @帳號 或 profile 網址（自動判斷平台），可一行一個批次貼上"
        style={inputStyle({ minHeight: 56, resize: 'vertical', fontFamily: HR.sans })}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
        <div style={{ width: 130 }}>
          <Select value={p.addPriority} allowEmpty={false} onChange={(v) => p.setAddPriority(v || 'B')}
            options={[{ value: 'A', label: '優先度 A' }, { value: 'B', label: '優先度 B' }, { value: 'C', label: '優先度 C' }]} />
        </div>
        {/* 平台選擇器 — 自動判斷切換時高亮提醒（platformFlash） */}
        <div style={{
          width: 150, borderRadius: uiPick(6, GxT.radius.input), transition: 'box-shadow 200ms',
          boxShadow: p.platformFlash ? `0 0 0 3px ${GxCore.alpha(HR.accent, '44')}` : 'none',
        }}>
          <Select value={p.addPlatform} allowEmpty={false} onChange={(v) => p.setAddPlatform(v || 'twitter')}
            options={[
              { value: 'twitter', label: 'X / Twitter' },
              { value: 'threads', label: '脆 Threads' },
              { value: 'youtube', label: 'YouTube' },
            ]} />
        </div>
        {p.platformFlash && (
          <span style={{ fontSize: FontSizes.trading_lg, color: HR.accent }}>已自動切換平台，請確認</span>
        )}
        <span style={{ flex: 1 }} />
        <Button variant="primary" size="sm" onClick={p.onAddSubmit} disabled={p.addBusy}>新增</Button>
      </div>
      <div
        onClick={() => !p.ocrBusy && p.fileRef.current && p.fileRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); if (!p.ocrBusy) p.setDragOver(true); }}
        onDragLeave={() => p.setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); p.setDragOver(false); if (p.ocrBusy) return; const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) p.onPickImage(f); }}
        style={{
          marginTop: 10, border: `1.5px dashed ${p.dragOver ? HR.accent : uiPick(HR.rule, GxT.color.ol)}`, borderRadius: uiPick(6, GxT.radius.inner),
          padding: 16, textAlign: 'center', fontSize: FontSizes.xs,
          cursor: p.ocrBusy ? 'default' : 'pointer', opacity: p.ocrBusy ? 0.75 : 1,
          color: p.dragOver ? HR.accent : HR.inkFaint, background: p.dragOver ? HR.accentSoft : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        {p.ocrBusy ? (
          <><Spinner size={15} /> OCR 解析中…</>
        ) : (
          <><Icon name="upload" size={15} /> 拖曳 / Ctrl+V 貼上個人頁截圖（OCR 解析帳號）</>
        )}
      </div>
      <input ref={p.fileRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) p.onPickImage(f); e.target.value = ''; }} />
    </div>
  );
}

function ET_ExpertList(p) {
  const [q, setQ] = React.useState('');
  const all = p.experts || [];
  const query = q.trim().toLowerCase();
  const list = query
    ? all.filter((e) => (e.handle + ' ' + (e.display_name || '')).toLowerCase().includes(query))
    : all;

  return (
    <Card className="et-fill" padding="0" eyebrow="Tracking" title="追蹤中" italic={p.experts ? `${list.length}${query ? `/${all.length}` : ''} 位` : '載入中…'}>
      {/* 搜尋框 — 帳號一多就靠這個 */}
      {all.length > 5 && (
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${HR.ruleSoft}` }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: HR.inkFaint, display: 'flex', pointerEvents: 'none' }}>
              <Icon name="search" size={13} />
            </span>
            <input
              type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="搜尋帳號…" aria-label="搜尋追蹤中的帳號"
              style={inputStyle({ paddingLeft: 28, paddingTop: 6, paddingBottom: 6, fontSize: FontSizes.xxs })}
            />
          </div>
        </div>
      )}

      {p.experts === null ? (
        <div style={{ padding: '10px 12px' }}><SkeletonTable rows={8} cols={['1fr']} header={false} /></div>
      ) : all.length === 0 ? (
        <EmptyState
          icon="user" title="還沒有追蹤任何神人"
          hint={p.canWrite
            ? '從新增神人開始 — 貼上帳號/網址，或拖一張個人頁截圖用 OCR 解析。'
            : '請聯繫管理員新增追蹤帳號。'}
          action={p.canWrite ? <Button variant="primary" size="sm" icon="plus" onClick={p.onOpenAdd}>新增神人</Button> : undefined}
        />
      ) : list.length === 0 ? (
        <EmptyState icon="search" title="找不到符合的帳號" hint="試試其他關鍵字。" />
      ) : (
        <div className="et-scroll">
          {list.map((e) => {
            const active = e.handle === p.selHandle;
            const muted = !e.has_account || e.disabled;
            return (
              <div
                key={e.handle} onClick={() => p.setSelHandle(e.handle)}
                style={{
                  position: 'relative', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', cursor: 'pointer', borderTop: `1px solid ${HR.ruleSoft}`,
                  // GX: selected row = tonal primary container (border width kept → no layout shift)
                  background: active ? uiPick(HR.surfaceAlt, GxT.color.pc) : 'transparent',
                  borderLeft: `3px solid ${active ? HR.accent : 'transparent'}`,
                  opacity: muted ? 0.55 : 1,
                }}
              >
                <ET_Avatar name={e.display_name} handle={e.handle} size={36} src={e.avatar_url} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <span style={{ fontFamily: HR.serif, fontSize: FontSizes.sm, fontWeight: uiPick(600, GxT.weight.heading), color: HR.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.display_name}</span>
                    {/* 優先度徽章（A/B/C = 更新頻率） */}
                    <span title={ET_PRIO_LABEL[e.priority] || ''} style={{
                      fontFamily: HR.mono, fontSize: FontSizes.trading_sm, fontWeight: 700, flexShrink: 0,
                      padding: '0 5px', borderRadius: uiPick(4, GxT.radius.chipSm), lineHeight: 1.7,
                      background: e.priority === 'A'
                        ? uiPick(HR.accentSoft, active ? GxT.color.card : GxCore.tone('accent').bg)
                        : uiPick(HR.ruleSoft, GxCore.tone('neutral').bg),
                      color: e.priority === 'A' ? HR.accent : HR.inkSoft,
                    }}>{e.priority || 'B'}</span>
                  </div>
                  {/* faint on the tonal pc row is < 4.5:1 → onV there (plan §2(d)-3a) */}
                  <div style={{ fontSize: FontSizes.trading_lg, color: active ? uiPick(HR.inkFaint, GxT.color.onV) : HR.inkFaint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>@{e.handle}</div>
                  <div style={{ marginTop: 3 }}><ET_Platforms list={e.platforms} /></div>
                </div>
                {!!e.new_posts && (
                  <span title={`過去 24 小時發文 ${e.new_posts} 篇`} style={{ fontSize: FontSizes.trading_lg, fontWeight: 700, color: '#fff', background: HR.accent, borderRadius: uiPick(10, GxT.radius.chip), padding: '1px 7px' }}>+{e.new_posts}</span>
                )}
                {p.canWrite && (
                <button
                  onClick={(ev) => { ev.stopPropagation(); p.setMenuFor(p.menuFor === e.handle ? null : e.handle); }}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: uiPick(HR.inkFaint, GxT.color.onV), display: 'flex', padding: uiPick(3, 6), borderRadius: uiPick(4, GxT.radius.btnSm) }}
                  aria-label={`@${e.handle} 更多動作`} aria-haspopup="menu" aria-expanded={p.menuFor === e.handle}
                ><Icon name="more" size={16} /></button>
                )}
                {p.canWrite && p.menuFor === e.handle && (
                  <div role="menu" onClick={(ev) => ev.stopPropagation()} style={uiStyle({
                    position: 'absolute', top: '100%', right: 8, zIndex: 30,
                    background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6,
                    boxShadow: '0 6px 20px rgba(14,20,17,0.16)', overflow: 'hidden', minWidth: 96,
                  }, { border: 'none', borderRadius: GxT.radius.inner, boxShadow: GxT.shadow.pop, padding: '4px 0' })}>
                    {e.disabled
                      ? <ET_MenuBtn onClick={() => p.onExpertAction('enable', e.handle)}>復原</ET_MenuBtn>
                      : <ET_MenuBtn onClick={() => p.onExpertAction('disable', e.handle)}>停用</ET_MenuBtn>}
                    <ET_MenuBtn danger onClick={() => p.onExpertAction('remove', e.handle)}>移除</ET_MenuBtn>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
function ET_MenuBtn({ children, onClick, danger }) {
  return (
    <button role="menuitem" onClick={onClick} style={uiStyle({
      display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'none',
      padding: '9px 14px', fontSize: FontSizes.xs, cursor: 'pointer', fontFamily: HR.sans,
      color: danger ? HR.down : HR.ink,
    }, { padding: '10px 16px', fontWeight: GxT.weight.medium })}
      onMouseEnter={(e) => { e.currentTarget.style.background = HR.surfaceAlt; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
    >{children}</button>
  );
}

const ET_WIN_OPTIONS = [
  { value: '1d', label: '1 天' }, { value: '3d', label: '3 天' },
  { value: '7d', label: '7 天' }, { value: 'all', label: '全部' },
];

function ET_Feed(p) {
  const e = p.selected;
  // 官方嵌入失敗的貼文（url 集合）→ 退回截圖呈現
  const [failedEmbeds, setFailedEmbeds] = React.useState(() => new Set());
  React.useEffect(() => { setFailedEmbeds(new Set()); }, [p.postState.posts]);
  const markEmbedFail = React.useCallback((url) => {
    setFailedEmbeds((s) => { const n = new Set(s); n.add(url); return n; });
  }, []);

  // lightbox：只涵蓋「以截圖呈現」的貼文（無官方嵌入、或嵌入失敗退回截圖者）
  const zoomable = React.useMemo(
    () => (p.postState.posts || [])
      .map((post) => ({ post, src: ET_imgSrc(post.screenshot_url) }))
      .filter((x) => x.src && (!ET_embedKind(x.post.url) || failedEmbeds.has(x.post.url))),
    [p.postState.posts, failedEmbeds],
  );
  const [zoomIdx, setZoomIdx] = React.useState(null);
  React.useEffect(() => { setZoomIdx(null); }, [p.postState.posts]);

  // 貼文捲動容器：捲下去出現「回到頂部」；換作者/時間窗自動回頂
  const scrollRef = React.useRef(null);
  const [showTop, setShowTop] = React.useState(false);
  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setShowTop(false);
  }, [p.postState.posts]);

  React.useEffect(() => {
    if (zoomIdx === null) return;
    const onKey = (ev) => {
      if (ev.key === 'Escape') setZoomIdx(null);
      else if (ev.key === 'ArrowLeft') setZoomIdx((i) => (i > 0 ? i - 1 : i));
      else if (ev.key === 'ArrowRight') setZoomIdx((i) => (i < zoomable.length - 1 ? i + 1 : i));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [zoomIdx, zoomable.length]);

  if (!e) {
    return (
      <div style={uiStyle(
        { background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6, height: '100%', minHeight: 360 },
        { border: 'none', borderRadius: GxT.radius.card },
      )}>
        <EmptyState icon="user" title="選一位神人" hint="從左側清單點一位，看他最新的貼文。" />
      </div>
    );
  }
  return (
    <div style={uiStyle({
      background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6,
      padding: '20px 24px', position: 'relative',
      height: '100%', minHeight: 360, display: 'flex', flexDirection: 'column',
    }, { border: 'none', borderRadius: GxT.radius.card })}>
      {/* 作者 header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 14 }}>
        <ET_Avatar name={e.display_name} handle={e.handle} size={52} src={e.avatar_url} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <h2 style={uiStyle(
            { fontFamily: HR.serif, fontSize: FontSizes.lg, fontWeight: 400, margin: 0, letterSpacing: '-0.015em' },
            { fontWeight: GxT.weight.title, letterSpacing: '-0.01em' },
          )}>{e.display_name}</h2>
          <div style={{ fontSize: FontSizes.trading_xl, color: HR.inkSoft, marginTop: 2 }}>@{e.handle} · {e.category}</div>
          <div style={{ marginTop: 5 }}><ET_Platforms list={e.platforms} /></div>
        </div>
        {!!e.new_posts && <Pill tone="accent">近 24 小時 +{e.new_posts}</Pill>}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 14, borderBottom: `1px solid ${HR.rule}`, flexWrap: 'wrap' }}>
        {/* 時間窗：分段快切（最常用的操作，一鍵切換） */}
        <ET_Segmented options={ET_WIN_OPTIONS} value={p.postWin} onChange={p.setPostWin} />
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>更新頻率</span>
        {p.canWrite ? (
          <div style={{ width: 132 }}>
            <Select value={e.priority || 'B'} allowEmpty={false} onChange={(v) => v && p.onPriorityChange(e.handle, v)}
              options={[{ value: 'A', label: ET_PRIO_LABEL.A }, { value: 'B', label: ET_PRIO_LABEL.B }, { value: 'C', label: ET_PRIO_LABEL.C }]} />
          </div>
        ) : (
          <span style={{ fontSize: FontSizes.trading_xl, color: HR.inkSoft, fontFamily: HR.mono }}>
            {ET_PRIO_LABEL[e.priority || 'B']}
          </span>
        )}
      </div>

      {p.postState.loading ? (
        <SkeletonTable rows={4} cols={['1fr']} header={false} />
      ) : p.postState.posts.length === 0 ? (
        <EmptyState
          icon="file-text" title="此時間窗內沒有貼文"
          hint={p.postWin === 'all'
            ? '這個帳號還沒有任何庫存貼文，按「立即更新」重抓。'
            : '這位作者近期沒有新內容 — 他過去的貼文仍在庫裡。'}
          action={p.postWin !== 'all' && (
            <Button variant="secondary" size="sm" onClick={() => p.setPostWin('all')}>顯示全部貼文</Button>
          )}
        />
      ) : (
        // 固定高度 + 內部捲軸：貼文再多也不把頁面往下撐（header/控制列保持固定）
        <div
          ref={scrollRef}
          className="et-scroll"
          onScroll={(ev) => setShowTop(ev.currentTarget.scrollTop > 400)}
          style={{ overflowX: 'hidden', paddingRight: 8 }}
        >
          {p.postState.posts.map((post, i) => (
            <ET_Post
              key={post.url || i} post={post}
              eager={i < 3}   // 前 3 篇立即渲染，其餘捲近才載（解延遲感）
              embedFailed={failedEmbeds.has(post.url)}
              onEmbedFail={() => markEmbedFail(post.url)}
              onResearch={
                // YouTube 貼文不給深入研究（它的動作是生成教材；text 只是
                // 影片標題等 metadata，不適合當研究主題）— 2026-08-06 Ray 指示
                p.onResearch && (post.text || '').trim() && ET_embedKind(post.url) !== 'youtube'
                  ? () => p.onResearch(post) : null
              }
              onCourseware={p.onCourseware && ET_embedKind(post.url) === 'youtube' ? () => p.onCourseware(post) : null}
              onYtDaily={p.onYtDaily && ET_embedKind(post.url) === 'youtube' ? () => p.onYtDaily(post) : null}
              onZoom={() => {
                const idx = zoomable.findIndex((x) => x.post === post);
                if (idx >= 0) setZoomIdx(idx);
              }}
            />
          ))}
        </div>
      )}

      {/* 回到頂部 — 捲超過 400px 才浮現，固定在卡片右下角 */}
      {showTop && (
        <button
          onClick={() => scrollRef.current && scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' })}
          title="回到頂部" aria-label="回到頂部"
          style={uiStyle({
            position: 'absolute', right: 18, bottom: 18, zIndex: 5,
            width: 38, height: 38, borderRadius: '50%', cursor: 'pointer',
            border: `1px solid ${HR.rule}`, background: HR.surface, color: HR.accent,
            boxShadow: '0 3px 12px rgba(14,20,17,0.18)', fontSize: 17, lineHeight: 1,
            display: 'grid', placeItems: 'center',
          }, {
            // small FAB (mockup .rfab language): rounded square, no outline
            width: 44, height: 44, borderRadius: GxT.radius.btn, border: 'none',
            boxShadow: '0 3px 8px rgba(0,0,0,.14)', fontWeight: GxT.weight.title,
          })}
          onMouseEnter={(ev) => { ev.currentTarget.style.background = HR.accentSoft; }}
          onMouseLeave={(ev) => { ev.currentTarget.style.background = HR.surface; }}
        >↑</button>
      )}

      {/* lightbox（feed 層）：點背景/Esc 關閉、←/→ 或畫面箭頭切換 */}
      {zoomIdx !== null && zoomable[zoomIdx] && (
        <ET_Lightbox
          item={zoomable[zoomIdx]}
          index={zoomIdx} total={zoomable.length}
          onClose={() => setZoomIdx(null)}
          onPrev={() => setZoomIdx((i) => Math.max(0, i - 1))}
          onNext={() => setZoomIdx((i) => Math.min(zoomable.length - 1, i + 1))}
        />
      )}
    </div>
  );
}

function ET_Lightbox({ item, index, total, onClose, onPrev, onNext }) {
  const navBtn = (side, disabled, onClick, glyph) => (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      disabled={disabled} aria-label={side === 'left' ? '上一張' : '下一張'}
      style={{
        position: 'fixed', top: '50%', [side]: 18, transform: 'translateY(-50%)',
        width: 44, height: 44, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.25)',
        background: 'rgba(14,20,17,0.65)', color: '#fff', fontSize: 20, cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.3 : 1, display: 'grid', placeItems: 'center',
      }}
    >{glyph}</button>
  );
  return (
    <div
      onClick={onClose} role="dialog" aria-label="圖片放大檢視"
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(14,20,17,0.78)', cursor: 'zoom-out',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 28,
      }}
    >
      <img src={item.src} alt="貼文圖片（放大）"
        style={{
          maxWidth: '88vw', maxHeight: '88vh', width: 'auto', height: 'auto',
          borderRadius: uiPick(8, GxT.radius.card), boxShadow: '0 16px 56px rgba(0,0,0,0.55)', background: '#fff',
        }} />
      {navBtn('left', index <= 0, onPrev, '‹')}
      {navBtn('right', index >= total - 1, onNext, '›')}
      <div style={{
        position: 'fixed', bottom: 22, left: '50%', transform: 'translateX(-50%)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{ color: 'rgba(255,255,255,0.75)', fontFamily: HR.mono, fontSize: FontSizes.xxs }}>
          {index + 1} / {total}
        </span>
        {item.post.url && (
          <a href={item.post.url} target="_blank" rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              color: '#fff', background: 'rgba(14,20,17,0.85)', border: '1px solid rgba(255,255,255,0.25)',
              padding: '8px 18px', borderRadius: 20, textDecoration: 'none', fontSize: FontSizes.xs,
            }}>
            開啟原文 ↗
          </a>
        )}
      </div>
    </div>
  );
}

function ET_Post({ post, onZoom, onEmbedFail, embedFailed, onResearch, onCourseware, onYtDaily, eager = false }) {
  const when = ET_when(post.posted_at || post.collected_at);
  const src = ET_imgSrc(post.screenshot_url);
  const [imgFailed, setImgFailed] = React.useState(false);
  const kind = ET_embedKind(post.url);
  const useEmbed = kind && !embedFailed;   // 官方嵌入為主要呈現；失敗才退截圖
  const hasImg = src && !imgFailed;

  const embedEl = !useEmbed ? null
    : kind === 'youtube' ? <ET_YouTubeEmbed url={post.url} />
    : kind === 'tweet' ? <ET_ScaledBox><ET_TweetEmbed url={post.url} onFail={onEmbedFail} /></ET_ScaledBox>
    : <ET_ScaledBox><ET_ThreadsEmbed url={post.url} onFail={onEmbedFail} /></ET_ScaledBox>;
  return (
    <article style={{ borderBottom: `1px solid ${HR.ruleSoft}`, padding: '14px 0', overflow: 'hidden' }}>
      {/* meta 列：時間（+轉推）｜右側獨立的原文連結 */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, fontSize: FontSizes.trading_lg, color: HR.inkFaint, flexWrap: 'wrap' }}>
        <span>{when}</span>
        {post.is_repost && <Pill tone="accent">🔁 轉推 @{post.author || ''}</Pill>}
        <span style={{ flex: 1 }} />
        {/* 生成日報 — YouTube 貼文單片走 yt_daily 完整管線（onYtDaily 只在
            「admin + YouTube 貼文」時由上層傳入，見 ExpertTrackerPage） */}
        {onYtDaily && (
          <button
            onClick={onYtDaily}
            title="把這部影片單獨跑一次 YT 日報管線（轉錄+判定+產報告）"
            style={{
              border: 'none', background: 'none', cursor: 'pointer', padding: 0,
              color: HR.accent, fontSize: FontSizes.trading_xl, whiteSpace: 'nowrap', fontFamily: 'inherit',
            }}>
            📰 生成日報
          </button>
        )}
        {/* 生成教材 — YouTube 貼文丟給 YTKB 分級教材管線（onCourseware 只在
            「可用影音知識庫 + YouTube 貼文」時由上層傳入，見 ExpertTrackerPage） */}
        {onCourseware && (
          <button
            onClick={onCourseware}
            title="把這部影片丟給影音知識庫生成分級教材"
            style={{
              border: 'none', background: 'none', cursor: 'pointer', padding: 0,
              color: HR.accent, fontSize: FontSizes.trading_xl, whiteSpace: 'nowrap', fontFamily: 'inherit',
            }}>
            🎓 生成教材
          </button>
        )}
        {/* 深入研究 — 丟給 HSG 研究引擎（onResearch 只在「員工 + 貼文有文字
            + 非 YouTube」時由上層傳入；YouTube 貼文的動作是「生成教材」、
            文字只是影片標題等 metadata，不適合當研究主題 — 見 ET_Feed） */}
        {onResearch && (
          <button
            onClick={onResearch}
            title="把這篇貼文丟給研究引擎深入研究"
            style={{
              border: 'none', background: 'none', cursor: 'pointer', padding: 0,
              color: HR.accent, fontSize: FontSizes.trading_xl, whiteSpace: 'nowrap', fontFamily: 'inherit',
            }}>
            🔍 深入研究
          </button>
        )}
        {post.url && (
          <a href={post.url} target="_blank" rel="noopener noreferrer"
            style={{ color: HR.accent, textDecoration: 'none', fontSize: FontSizes.trading_xl, whiteSpace: 'nowrap' }}>
            原文 ↗
          </a>
        )}
      </div>

      {/* 主要呈現：官方嵌入（YT 播放器 / X 推文 / Threads 貼文）。
          前幾篇 eager 立即渲染；其餘懶掛載（捲近才載，避免全部同時搶資源） */}
      {useEmbed ? (
        eager ? embedEl : (
          <ET_LazyMount placeholderHeight={kind === 'youtube' ? 300 : 240}>{embedEl}</ET_LazyMount>
        )
      ) : hasImg ? (
        // 備援：截圖（點圖 → 懸浮放大，feed 層 lightbox 可前後切換）
        <img src={src} alt="貼文截圖" loading="lazy"
          onClick={onZoom}
          onError={() => setImgFailed(true)}
          title="點擊放大"
          style={{
            width: '100%', maxWidth: 480, height: 'auto', cursor: 'zoom-in',
            border: `1px solid ${HR.rule}`, borderRadius: uiPick(6, GxT.radius.inner), display: 'block',
          }} />
      ) : (
        <div style={{ fontSize: FontSizes.trading_xl, color: HR.inkFaint, fontStyle: uiPick('italic', 'normal') }}>（無內容 — 點右上「原文」查看）</div>
      )}
    </article>
  );
}

function ET_Recs(p) {
  return (
    <Card className="et-fill" padding="0" eyebrow="Discovery" title="推薦追蹤" italic="依提及次數">
      {p.recs === null ? (
        <div style={{ padding: '10px 16px' }}><SkeletonTable rows={4} cols={['1fr']} header={false} /></div>
      ) : (
        <div className="et-scroll" style={{ padding: '4px 0' }}>
          {p.recs.map((r, i) => (
            <div key={r.handle} style={{ padding: '14px 16px', borderTop: i ? `1px solid ${HR.ruleSoft}` : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <ET_Avatar name={r.display_name} handle={r.handle} size={38} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontFamily: HR.serif, fontSize: FontSizes.sm, fontWeight: uiPick(600, GxT.weight.heading), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>@{r.handle}</div>
                  <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>{r.category}</div>
                </div>
                {i === 0 && <Pill tone="accent">最推</Pill>}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '9px 0' }}>
                <Pill tone="accent">分 {r.score}</Pill>
                <Pill>出現 {r.appearances}</Pill>
                {!!r.distinct_topics && <Pill>{r.distinct_topics} 主題</Pill>}
              </div>
              {r.sample && <div style={{ fontFamily: HR.serif, fontSize: FontSizes.sm, color: HR.inkSoft, marginBottom: 10 }}>「{r.sample}」</div>}
              {p.canWrite && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <Button variant="primary" size="sm" onClick={() => p.onRecAction('sub', r.handle)} style={{ flex: 1, justifyContent: 'center' }}>訂閱</Button>
                  <Button variant="ghost" size="sm" onClick={() => p.onRecAction('dis', r.handle)}>忽略</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ───────────────────────── 追蹤統計 ─────────────────────────
function ET_Stats({ stats, onOpenAuthor }) {
  if (stats === null) {
    return <div><SkeletonTable rows={6} cols={['1fr']} header={false} /></div>;
  }
  const ranked = stats.ranked || [];
  const max = (ranked[0] && ranked[0].dwell_ms) || 1;
  const cards = [
    { label: '總閱讀時間', value: stats.total_dwell_min != null ? `${stats.total_dwell_min} 分` : '—' },
    { label: '開啟明細次數', value: stats.total_opens != null ? stats.total_opens : '—' },
    { label: '最常看的大神', value: stats.top_handle || '—', small: true },
  ];
  return (
    <>
      <div style={{ fontSize: FontSizes.sm, color: HR.inkSoft, marginBottom: 14 }}>我的追蹤統計（過去 30 天）</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
        {cards.map((c) => (
          <div key={c.label} style={uiStyle(
            { background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 6, padding: '16px 20px' },
            { border: 'none', borderRadius: GxT.radius.card, padding: '18px 20px' },
          )}>
            <div style={uiStyle(
              { fontSize: FontSizes.trading_lg, color: HR.inkFaint, letterSpacing: '0.08em', textTransform: 'uppercase' },
              { fontSize: GxT.size.label, color: GxT.color.onV, letterSpacing: 0, textTransform: 'none', fontWeight: GxT.weight.label },
            )}>{c.label}</div>
            <div style={uiStyle(
              { fontFamily: HR.serif, fontSize: c.small ? FontSizes.lg : FontSizes.xl, fontWeight: 400, marginTop: 6, color: HR.ink, fontVariantNumeric: 'tabular-nums' },
              { fontWeight: GxT.weight.title, letterSpacing: '-0.01em', overflowWrap: 'anywhere' },
            )}>{c.value}</div>
          </div>
        ))}
      </div>
      <Card padding="8px 24px 20px" eyebrow="Ranking" title="閱讀停留排行" italic="依累計閱讀時間 · 點列查看貼文">
        {ranked.length === 0 ? (
          <EmptyState icon="clock" title="還沒有閱讀紀錄" hint="點進大神明細後就會開始統計。" />
        ) : (
          <div>
            {ranked.map((r, i) => (
              <div
                key={r.handle}
                onClick={() => onOpenAuthor && onOpenAuthor(r.handle)}
                title={`查看 @${r.handle} 的貼文`}
                style={{ display: 'flex', alignItems: 'center', gap: 13, padding: uiPick('13px 6px', '13px 10px'), borderBottom: `1px solid ${HR.ruleSoft}`, cursor: 'pointer', borderRadius: uiPick(4, GxT.radius.inner) }}
                onMouseEnter={(e) => { e.currentTarget.style.background = HR.surfaceAlt; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={uiStyle(
                  { fontFamily: HR.serif, fontSize: FontSizes.md, fontWeight: 600, color: HR.inkFaint, width: 22, textAlign: 'center' },
                  { fontWeight: GxT.weight.title, fontVariantNumeric: 'tabular-nums' },
                )}>{i + 1}</span>
                <ET_Avatar name={r.display_name} handle={r.handle} size={34} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: FontSizes.xs }}>
                    <span style={{ fontWeight: 500, color: HR.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.display_name}</span>
                    <span style={{ color: HR.inkFaint, whiteSpace: 'nowrap' }}>{r.dwell_min} 分 · 開 {r.opens} 次</span>
                  </div>
                  <div style={{ height: 7, background: uiPick(HR.surfaceAlt, GxT.color.s3), borderRadius: uiPick(4, GxT.radius.pill), overflow: 'hidden', marginTop: 6 }}>
                    <div style={{ height: '100%', width: `${Math.round((r.dwell_ms / max) * 100)}%`, background: HR.accent, borderRadius: uiPick(4, GxT.radius.pill) }} />
                  </div>
                </div>
                <Icon name="chevron-right" size={13} />
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

// ───────────────────────── YT 日報 ─────────────────────────
/* 最愛 YouTube 頻道每日基本面掃描（上游 InfoScout /api/yt_daily/*）：
 *   收藏頻道（/author/favorite ⭐ toggle）→ 上游每日掃描 RSS 新片 → YTKB 轉錄
 *   → DeepSeek 判定含個股/產業基本面 → 命中產 MD 報告。
 *   讀取（歷程/報告/排程/狀態）全帳號可用；寫入（收藏/排程/立即執行）僅
 *   admin —— proxy 層亦強制（routes_infoscout 非 GET 限 admin）。
 *   報告 markdown 含 YAML frontmatter（API 不剝），前端剝掉後用 MarkdownView
 *   渲染；個股/產業 chips 直接取自 verdict，不用另 parse frontmatter。 */

const ET_YD_STATUS = {
  hit:     { label: '命中',   tone: 'accent' },
  miss:    { label: '未命中', tone: 'neutral' },
  error:   { label: '失敗',   tone: 'down' },
  skipped: { label: '跳過',   tone: 'warn' },
  deleted: { label: '已刪除', tone: 'neutral' },
};

function ET_ydStripFrontmatter(md) {
  const m = /^---\r?\n[\s\S]*?\r?\n---\r?\n?/.exec(md || '');
  return m ? md.slice(m[0].length) : (md || '');
}

// 掃描歷程分組用日期（processed_at UTC → 瀏覽器本地日期）
function ET_ydDate(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return (iso || '').slice(0, 10) || '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
}


// 已讀狀態:localStorage 記 video_id(僅本瀏覽器,失效不影響功能,上限 500 筆)。
const ET_YD_READ_KEY = 'etyd.read';
function ET_ydLoadRead() {
  try { return new Set(JSON.parse(localStorage.getItem(ET_YD_READ_KEY) || '[]')); }
  catch (e) { return new Set(); }
}
function ET_ydSaveRead(set) {
  try { localStorage.setItem(ET_YD_READ_KEY, JSON.stringify(Array.from(set).slice(-500))); }
  catch (e) { /* noop */ }
}

function ET_YtDaily({ canWrite, isAdmin, toast, focusId }) {
  const [status, setStatus] = React.useState(null);       // /yt_daily/status
  const [schedule, setSchedule] = React.useState(null);   // /schedule/yt_daily
  const [channels, setChannels] = React.useState(null);   // YouTube 頻道(含 yt_favorite)
  const [videos, setVideos] = React.useState(null);       // 掃描歷程(newest-first)
  const [running, setRunning] = React.useState(false);
  const [query, setQuery] = React.useState('');           // 左欄搜尋(標題/頻道/個股)
  const [selId, setSelId] = React.useState(null);         // 右窗開啟中的報告
  const [histOpen, setHistOpen] = React.useState(false);  // 掃描歷程摺疊段
  const [histReasonId, setHistReasonId] = React.useState(null); // 歷程列展開理由
  const [favOpen, setFavOpen] = React.useState(false);    // 頻道管理 Modal
  const [confirmDel, setConfirmDel] = React.useState(false); // 刪除報告確認
  const [delBusy, setDelBusy] = React.useState(false);
  const [reports, setReports] = React.useState({});       // video_id → {loading|md|error}
  const [readSet, setReadSet] = React.useState(ET_ydLoadRead);
  const reportsRef = React.useRef({});                    // reports 的同步鏡像(快取判斷用)
  const pollRef = React.useRef(null);

  const loadVideos = React.useCallback(() => {
    api('GET', '/infoscout/yt_daily/videos?limit=200')
      .then((d) => setVideos((d && d.videos) || []))
      .catch(() => setVideos([]));
  }, []);

  const loadChannels = React.useCallback(() => {
    api('GET', '/infoscout/experts?window=all&sort=name')
      .then((d) => setChannels(((d && d.experts) || []).filter((e) => (e.platforms || []).includes('youtube'))))
      .catch(() => setChannels([]));
  }, []);

  function startPoll() {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      let s;
      try { s = await api('GET', '/infoscout/yt_daily/status'); } catch { return; }
      setStatus(s);
      if (s && s.running) return;
      clearInterval(pollRef.current); pollRef.current = null;
      setRunning(false);
      if (s && s.summary) toast('YT 日報掃描完成:' + s.summary, s.ok === false ? 'error' : 'success');
      loadVideos();
    }, 3000);
  }

  React.useEffect(() => {
    api('GET', '/infoscout/yt_daily/status').then((s) => {
      setStatus(s);
      if (s && s.running) { setRunning(true); startPoll(); }
    }).catch(() => {});
    api('GET', '/infoscout/schedule/yt_daily').then(setSchedule).catch(() => {});
    loadChannels();
    loadVideos();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 開啟報告(左列表/歷程命中列共用):標已讀 + lazy 載入 + 快取 ──
  function openReport(id) {
    setSelId(id);
    setReadSet((s) => {
      if (s.has(id)) return s;
      const next = new Set(s); next.add(id); ET_ydSaveRead(next); return next;
    });
    if (reportsRef.current[id]) return;
    reportsRef.current = { ...reportsRef.current, [id]: { loading: true } };
    setReports(reportsRef.current);
    api('GET', `/infoscout/yt_daily/report?video_id=${encodeURIComponent(id)}`)
      .then((d) => {
        reportsRef.current = { ...reportsRef.current, [id]: { md: ET_ydStripFrontmatter((d && d.markdown) || '') } };
        setReports(reportsRef.current);
      })
      .catch((e) => {
        reportsRef.current = { ...reportsRef.current, [id]: { error: (e && e.detail) || '報告載入失敗' } };
        setReports(reportsRef.current);
      });
  }

  // ── 收藏 toggle(= 加入/退出每日掃描名單;Modal 內,admin 專屬)──
  async function toggleFav(handle, favorite) {
    const res = await ET_write('/infoscout/author/favorite', { handle, favorite });
    if (res.ok) {
      setChannels((cs) => (cs || []).map((c) => (c.handle === handle ? { ...c, yt_favorite: favorite } : c)));
      toast(favorite ? `已收藏 @${handle},加入每日掃描` : `已取消收藏 @${handle}`, 'success');
    } else {
      const map = { not_youtube: '不是 YouTube 頻道', not_found: '找不到此帳號' };
      toast('操作失敗:' + (map[res.reason] || res.reason || ''), 'error');
    }
  }

  // ── 立即執行 ──
  async function onRunNow() {
    setRunning(true);
    const res = await ET_write('/infoscout/yt_daily/run', {});
    if (!res.ok && res.reason === 'already_running') toast('已經在掃描中…');
    else if (!res.ok) { setRunning(false); toast('啟動失敗:' + (res.reason || res.detail || ''), 'error'); return; }
    else toast('開始掃描收藏頻道的新影片(含轉錄與判定,可能需要數十分鐘)');
    startPoll();
  }

  // ── 刪除報告(admin;上游軟刪:標 deleted、刪 MD 檔、去重保留不復活)──
  async function doDeleteReport() {
    const id = selId;
    if (!id) return;
    setDelBusy(true);
    const res = await ET_write('/infoscout/yt_daily/delete', { video_id: id });
    setDelBusy(false);
    setConfirmDel(false);
    if (!res.ok) {
      toast(res.status === 404 && !res.reason
        ? '刪除失敗:上游 InfoScout 尚未支援刪除端點,請先更新部署'
        : '刪除失敗:' + (res.reason === 'not_found' ? '找不到這份報告' : (res.reason || res.detail || '')), 'error');
      return;
    }
    toast('已刪除報告', 'success');
    delete reportsRef.current[id];
    setReports({ ...reportsRef.current });
    const idx = filtered.findIndex((v) => v.video_id === id);
    const next = filtered[idx + 1] || filtered[idx - 1] || null;
    if (next && next.video_id !== id) openReport(next.video_id); else setSelId(null);
    loadVideos();
  }

  // ── 每日排程 ──
  async function saveSchedule(next) {
    const body = { enabled: !!next.enabled, at: next.at || '07:30' };
    const res = await ET_write('/infoscout/schedule/yt_daily', body);
    if (res.ok) {
      setSchedule({ ...(schedule || {}), ...(res.data || body) });
      toast(body.enabled ? `每日 ${body.at} 自動掃描` : '已關閉每日自動掃描', 'success');
    } else {
      toast('排程設定失敗' + (res.reason === 'invalid_at' ? ':時間格式錯誤' : ''), 'error');
    }
  }

  // ── 衍生資料:命中報告(主角)、搜尋過濾、日期分組 ──
  const hits = React.useMemo(() => (videos || []).filter((v) => v.status === 'hit'), [videos]);
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return hits;
    return hits.filter((v) => {
      const stocks = ((v.verdict && v.verdict.stocks) || []).join(' ');
      return `${v.title || ''} ${v.handle || ''} ${stocks}`.toLowerCase().indexOf(q) >= 0;
    });
  }, [hits, query]);
  const groups = React.useMemo(() => {
    const out = [];
    let cur = null;
    for (const v of filtered) {
      const date = ET_ydDate(v.processed_at);
      if (!cur || cur.date !== date) { cur = { date, items: [] }; out.push(cur); }
      cur.items.push(v);
    }
    return out;
  }, [filtered]);
  const sel = React.useMemo(
    () => (videos || []).find((v) => v.video_id === selId) || null,
    [videos, selId],
  );

  // 進頁自動打開:大神牆「生成日報」指定的那份(focusId)優先,否則最新一份
  React.useEffect(() => {
    if (selId || !hits.length) return;
    const focus = focusId && hits.find((v) => v.video_id === focusId);
    openReport(focus ? focus.video_id : hits[0].video_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hits]);

  // ↑↓ 鍵切換報告(焦點在輸入框時不攔截)
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (!filtered.length) return;
      const idx = filtered.findIndex((v) => v.video_id === selId);
      const next = e.key === 'ArrowDown'
        ? Math.min(idx + 1, filtered.length - 1)
        : Math.max(idx - 1, 0);
      if (next !== idx && filtered[next]) { e.preventDefault(); openReport(filtered[next].video_id); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, selId]);

  const st = status || {};
  const statusLine = st.running
    ? '掃描中…(每 3 秒更新狀態)'
    : st.finished_at
      ? `上次執行:${ET_relTime(st.finished_at)}${st.summary ? ` · ${st.ok === false ? '❌ ' : ''}${st.summary}` : ''}`
      : '尚未執行過';
  const nextLine = (schedule && schedule.enabled && schedule.next_run_at) ? ` · 下次排程:${ET_when(schedule.next_run_at)}` : '';
  const selRep = selId ? reports[selId] : null;

  return (
    <>
      {/* 控制列:說明 + 排程 + 頻道管理 + 立即執行(寫入僅 admin) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={{ fontSize: FontSizes.sm, color: HR.inkSoft, flex: 1, minWidth: 260 }}>
          最愛 YouTube 頻道每日基本面掃描 — 命中個股/產業觀點的影片會自動產出報告。
        </div>
        {isAdmin && schedule && (
          <>
            <ET_Segmented
              size="sm" value={schedule.enabled ? 'on' : 'off'}
              onChange={(v) => saveSchedule({ enabled: v === 'on', at: schedule.at })}
              options={[{ value: 'on', label: '每日排程' }, { value: 'off', label: '關閉' }]}
            />
            {!!schedule.enabled && (
              <input
                type="time" value={schedule.at || '07:30'}
                onChange={(e) => saveSchedule({ enabled: true, at: e.target.value })}
                style={uiStyle({
                  fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.ink,
                  border: `1px solid ${HR.rule}`, borderRadius: 6, padding: '4px 8px', background: HR.surface,
                }, Object.assign(ET_gxField(), { minHeight: 32, padding: '0 10px' }))}
              />
            )}
          </>
        )}
        {canWrite && (
          <Button variant="ghost" size="sm" icon="user" onClick={() => setFavOpen(true)}>頻道管理</Button>
        )}
        {isAdmin && (
          <Button variant="primary" size="sm" icon="refresh" disabled={running} onClick={onRunNow}>
            {running ? '掃描中…' : '立即執行'}
          </Button>
        )}
      </div>
      <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginBottom: 14 }}>
        {statusLine}{nextLine}
      </div>

      <div className="et-yd-cols">
        {/* 左:命中報告列表(搜尋 + 日期分組 + 未讀紅點)+ 掃描歷程摺疊段 */}
        <Card padding="0" eyebrow="Reports" title="命中報告" italic={hits.length ? `${hits.length} 份` : ''}>
          <div style={{ padding: '10px 12px 6px' }}>
            <input
              value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="搜標題 / 頻道 / 個股…"
              aria-label={uiPick(undefined, '搜尋命中報告')}
              style={uiStyle({
                width: '100%', boxSizing: 'border-box', fontFamily: HR.sans,
                fontSize: FontSizes.xxs, color: HR.ink, border: `1px solid ${HR.rule}`,
                borderRadius: 6, padding: '6px 10px', background: HR.surfaceAlt, outline: 'none',
              }, Object.assign(ET_gxField(), { minHeight: 36, padding: '0 12px' }))}
            />
          </div>
          {videos === null ? (
            <div style={{ padding: '4px 12px 10px' }}><SkeletonTable rows={5} cols={['1fr']} header={false} /></div>
          ) : filtered.length === 0 ? (
            hits.length === 0 ? (
              <EmptyState icon="clock" title="還沒有命中報告" hint="收藏頻道後執行掃描,命中的影片會出現在這裡。" />
            ) : (
              <EmptyState icon="search" title="沒有符合的報告" hint="換個關鍵字試試。" />
            )
          ) : (
            <div style={{ maxHeight: 520, overflowY: 'auto', padding: '0 0 6px' }}>
              {groups.map((g) => (
                <div key={g.date}>
                  <div style={uiStyle({
                    padding: '9px 12px 4px', fontSize: FontSizes.trading_lg, color: HR.inkFaint,
                    letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600,
                  }, { padding: '12px 12px 4px', color: GxT.color.p, letterSpacing: 0, textTransform: 'none', fontWeight: GxT.weight.label, fontVariantNumeric: 'tabular-nums' })}>{g.date}</div>
                  {g.items.map((v) => {
                    const active = selId === v.video_id;
                    const unread = !readSet.has(v.video_id);
                    const stocks = ((v.verdict && v.verdict.stocks) || []).slice(0, 3);
                    return (
                      <div
                        key={v.video_id} onClick={() => openReport(v.video_id)}
                        style={{
                          padding: '8px 12px 9px 10px', cursor: 'pointer', position: 'relative',
                          background: active ? HR.accentSoft : 'transparent',
                          borderLeft: `2px solid ${active ? HR.accent : 'transparent'}`,
                        }}
                        onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = HR.surfaceAlt; }}
                        onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div style={{
                          fontSize: FontSizes.xs, fontWeight: unread ? 600 : 400,
                          color: HR.ink, paddingRight: 14,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{v.title || v.video_id}</div>
                        <div style={{ fontSize: FontSizes.trading_lg, color: active ? uiPick(HR.inkFaint, GxT.color.onV) : HR.inkFaint, marginTop: 2 }}>@{v.handle}</div>
                        {!!stocks.length && (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                            {stocks.map((s) => <Pill key={s} tone="accent">{s}</Pill>)}
                          </div>
                        )}
                        {unread && (
                          <span style={{
                            position: 'absolute', right: 10, top: 12, width: 7, height: 7,
                            borderRadius: '50%', background: uiPick('#C0342E', GxT.color.warm),
                          }} />
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
          {/* 掃描歷程摺疊段:全部狀態(含未命中/失敗/跳過)。點命中列 → 右窗開報告;
              點其他列 → inline 展開判定理由。 */}
          <div style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
            <button
              onClick={() => setHistOpen((o) => !o)}
              style={{
                width: '100%', textAlign: 'left', border: 'none', background: 'transparent',
                cursor: 'pointer', padding: '10px 12px', fontFamily: HR.sans,
                fontSize: FontSizes.xxs, color: HR.inkSoft, display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <Icon name={histOpen ? 'chevron-down' : 'chevron-right'} size={12} />
              掃描歷程({(videos || []).length})
            </button>
            {histOpen && (
              <div style={{ maxHeight: 260, overflowY: 'auto', paddingBottom: 6 }}>
                {(videos || []).map((v) => {
                  const meta = ET_YD_STATUS[v.status] || { label: v.status, tone: 'neutral' };
                  const reason = v.verdict && v.verdict.reason;
                  return (
                    <div key={v.video_id}>
                      <div
                        onClick={() => (v.status === 'hit'
                          ? openReport(v.video_id)
                          : setHistReasonId((cur) => (cur === v.video_id ? null : v.video_id)))}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', cursor: 'pointer' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = HR.surfaceAlt; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <span style={{ fontSize: FontSizes.trading_sm, color: HR.inkFaint, fontFamily: HR.mono, flexShrink: 0 }}>
                          {ET_ydDate(v.processed_at).slice(5)}
                        </span>
                        <span style={{
                          flex: 1, minWidth: 0, fontSize: FontSizes.trading_lg, color: HR.inkSoft,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{v.title || v.video_id}</span>
                        <Pill tone={meta.tone}>{meta.label}</Pill>
                      </div>
                      {histReasonId === v.video_id && !!reason && (
                        <div style={{ padding: '0 12px 8px 44px', fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
                          {reason}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

        {/* 右:閱讀窗(報告是主角) */}
        <Card padding="0" eyebrow="Report" title="報告閱讀" italic={sel ? `@${sel.handle}` : ''}>
          {!sel ? (
            videos === null ? (
              <div style={{ padding: '10px 16px' }}><SkeletonTable rows={6} cols={['1fr']} header={false} /></div>
            ) : (
              <EmptyState icon="book" title={hits.length ? '選一份報告' : '還沒有命中報告'}
                hint={hits.length ? '從左側列表點一份報告開始閱讀。' : '收藏頻道後執行掃描,命中的影片會自動產出報告。'} />
            )
          ) : (
            <div style={{ padding: '16px 24px 26px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0, fontFamily: HR.serif, fontSize: FontSizes.lg, fontWeight: uiPick(600, GxT.weight.title), color: HR.ink, lineHeight: 1.35 }}>
                  {sel.title || sel.video_id}
                </div>
                {canWrite && (
                  <Button variant="ghost" size="sm" icon="trash" onClick={() => setConfirmDel(true)}>刪除</Button>
                )}
              </div>
              <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, margin: '6px 0 10px' }}>
                @{sel.handle} · {ET_ydDate(sel.published_at || sel.processed_at)}
                {sel.url && (
                  <>
                    {' · '}
                    <a href={sel.url} target="_blank" rel="noopener noreferrer" style={{ color: HR.accent }}>
                      在 YouTube 開啟 ↗
                    </a>
                  </>
                )}
              </div>
              {!!(((sel.verdict && sel.verdict.stocks) || []).length || ((sel.verdict && sel.verdict.industries) || []).length) && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                  {((sel.verdict && sel.verdict.stocks) || []).map((s) => <Pill key={`s-${s}`} tone="accent">{s}</Pill>)}
                  {((sel.verdict && sel.verdict.industries) || []).map((s) => <Pill key={`i-${s}`}>{s}</Pill>)}
                </div>
              )}
              {!selRep || selRep.loading ? (
                <div style={{ padding: '10px 0' }}><SkeletonTable rows={5} cols={['1fr']} header={false} /></div>
              ) : selRep.error ? (
                <div style={{ fontSize: FontSizes.xs, color: uiPick('#B0512E', GxT.color.down), padding: '10px 0' }}>{selRep.error}</div>
              ) : (
                <div style={{ borderTop: `1px solid ${HR.ruleSoft}`, paddingTop: 16 }}>
                  <MarkdownView className="ar-markdown" markdown={selRep.md} />
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* 頻道管理 Modal(admin 專屬入口):⭐ = 進每日掃描名單 */}
      {favOpen && (
        <Modal
          open onClose={() => setFavOpen(false)} size="md"
          title="頻道管理"
          subtitle="⭐ 收藏的 YouTube 頻道會加入每日掃描名單"
          footer={<Button variant="ghost" onClick={() => setFavOpen(false)}>關閉</Button>}
        >
          {channels === null ? (
            <div style={{ padding: '6px 0' }}><SkeletonTable rows={4} cols={['1fr']} header={false} /></div>
          ) : channels.length === 0 ? (
            <EmptyState icon="user" title="沒有 YouTube 頻道" hint="先到大神牆新增 YouTube 神人,再回這裡收藏。" />
          ) : (
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              {channels.map((c, i) => (
                <div key={c.handle} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', borderTop: i ? `1px solid ${HR.ruleSoft}` : 'none' }}>
                  <button
                    title={c.yt_favorite ? '取消收藏(退出每日掃描)' : '收藏(加入每日掃描)'}
                    onClick={() => toggleFav(c.handle, !c.yt_favorite)}
                    style={{
                      border: 'none', background: 'transparent', padding: 2, lineHeight: 1,
                      fontSize: FontSizes.md, cursor: 'pointer', opacity: c.yt_favorite ? 1 : 0.4,
                    }}
                  >{c.yt_favorite ? '⭐' : '☆'}</button>
                  <ET_Avatar name={c.display_name} handle={c.handle} size={32} src={c.avatar_url} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: FontSizes.xs, fontWeight: 500, color: HR.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {c.display_name || c.handle}
                    </div>
                    <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>@{c.handle}</div>
                  </div>
                  {!!c.yt_favorite && <Pill tone="accent">每日掃描</Pill>}
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      <ConfirmDialog
        open={confirmDel}
        title="刪除這份報告"
        message={sel ? `將刪除「${sel.title || sel.video_id}」的報告。上游為軟刪:同一部影片不會再次產生報告。` : ''}
        danger busy={delBusy} confirmLabel="確認刪除"
        onCancel={() => setConfirmDel(false)}
        onConfirm={doDeleteReport}
      />
    </>
  );
}

window.ExpertTrackerPage = ExpertTrackerPage;
